import { cursorPaginationSchema } from './paginationSchema';
import { CHOICE_PICKER_ENTRY } from './choicePickerCatalog';
import { RECORD_EDITOR_OPTION_SCHEMA, RECORD_EDITOR_STRING_OPTION_SCHEMA, RECORD_EDITOR_OPTIONS_SOURCE_SCHEMA, RECORD_EDITOR_OPTION_TABLES_SCHEMA } from './recordEditorOptions';
import { BASE_ASSET_ICON_DESCRIPTOR_SCHEMA } from '../base/BaseAssetIcon';
import { ORDERED_SELECTION_MODEL_SCHEMA, ORDERED_SELECTION_INTENT_SCHEMAS } from './orderedSelection';
import { GALLERY_PRESENTATION_SETTINGS_SCHEMA, GALLERY_PRESENTATION_FIELDS_SCHEMA, GALLERY_PRESENTATION_SETTING_CHANGE_SCHEMA } from './galleryPresentationSettings';
import { RUNTIME_TERMINAL_MODEL_SCHEMA } from './runtimeTerminal';
import { TABLE_DISPLAY_CELL_SCHEMAS } from './typedDisplayCell';
import { I18N_DISPLAY_TEXT_SCHEMA, LOCALIZED_DISPLAY_PARAMETERS_SCHEMA } from './localizedDisplayValue';
import { FILE_PICKER_SCHEMA, FILE_PICKER_RENDER_MODEL_SCHEMA, FILE_PICKER_SELECT_INTENT_SCHEMA, FILE_PICKER_CANCEL_INTENT_SCHEMA } from './filePicker';
import { TABLE_MESSAGE_VALUE_SCHEMA } from './tableMessageValue';
import { NUMERIC_SERIES_SCHEMA, NUMERIC_SERIES_RENDER_MODEL_SCHEMA, NUMERIC_SERIES_RANGE_INTENT_SCHEMA, NUMERIC_SERIES_EXPAND_INTENT_SCHEMA } from "./numericSeries";
import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import { SUMMARY_CHART_SCHEMA, SUMMARY_CHART_RENDER_MODEL_SCHEMA } from './summaryChart';
import { DATA_EXPLORER_APPEARANCE_OPTIONS } from '../data-explorer/appearanceOptions';

import {
  compileDesignCapabilityCatalog,
  compileDesignCapabilityCatalogArtifact,
} from "./catalog";
import { DESIGN_CAPABILITY_I18N_KEYS } from "./i18n";
import {
  DATA_CONTEXT_BAR_CHANGE_INTENT_SCHEMA,
  DATA_CONTEXT_BAR_CLEAR_INTENT_SCHEMA,
  DATA_CONTEXT_BAR_REFRESH_INTENT_SCHEMA,
  DATA_CONTEXT_BAR_RENDER_MODEL_SCHEMA,
  DATA_CONTEXT_BAR_SCHEMA,
  ONTOLOGY_TREND_MATRIX_RENDER_MODEL_SCHEMA,
  ONTOLOGY_TREND_MATRIX_SCHEMA,
  ONTOLOGY_TREND_MATRIX_SELECT_INTENT_SCHEMA,
} from "./nativeAnalytics";
import {
  ONTOLOGY_MANAGEMENT_OUTPUT_PORTS,
  ONTOLOGY_MANAGEMENT_RENDER_MODEL_SCHEMA,
  ONTOLOGY_MANAGEMENT_SCHEMA,
} from "./ontologyManagement";
import {
  FEATURE_COLUMN_MANAGER_OUTPUT_PORTS,
  FEATURE_COLUMN_MANAGER_RENDER_MODEL_SCHEMA,
  FEATURE_COLUMN_MANAGER_SCHEMA,
} from "./featureColumnManager";

export const DESIGN_CAPABILITY_IDS = Object.freeze({
  choicePicker: "monkeys.design.choice-picker",
  shellLayout: "monkeys.design.shell-layout",
  sidebarFilter: "monkeys.design.sidebar-filter",
  ontologyNavigator: "monkeys.design.ontology-navigator",
  dataToolbar: "monkeys.design.data-toolbar",
  ontologyTable: "monkeys.design.ontology-table",
  numericSeries: "monkeys.design.numeric-series",
  summaryChart: "monkeys.design.summary-chart",
  filePicker: "monkeys.design.file-picker",
  runtimeTerminal: "monkeys.design.runtime-terminal",
  orderedSelection: "monkeys.design.ordered-selection",
  ontologyTrendMatrix: "monkeys.design.ontology-trend-matrix",
  dataContextBar: "monkeys.design.data-context-bar",
  cardGrid: "monkeys.design.card-grid",
  gallery: "monkeys.design.gallery",
  pagination: "monkeys.design.pagination",
  recordDetail: "monkeys.design.record-detail",
  recordEditor: "monkeys.design.record-editor",
  recordHistory: "monkeys.design.record-history",
  ontologyManagement: "monkeys.design.ontology-management",
  featureColumnManager: "monkeys.design.feature-column-manager",
  sectionHeader: "monkeys.design.section-header",
  notice: "monkeys.design.notice",
  descriptionList: "monkeys.design.description-list",
  contentPanel: "monkeys.design.content-panel",
  toolbar: "monkeys.design.toolbar",
  status: "monkeys.design.status",
  professionalLeaf: "monkeys.design.professional-leaf",
  professionalExtensionLeaf: "monkeys.design.professional-extension-leaf",
} as const);

export const DESIGN_CAPABILITY_RENDERER_KEYS = Object.freeze({
  shellLayout: "monkeys.design.renderer.shell-layout",
  sidebarFilter: "monkeys.design.renderer.sidebar-filter",
  ontologyNavigator: "monkeys.design.renderer.ontology-navigator",
  dataToolbar: "monkeys.design.renderer.data-toolbar",
  ontologyTable: "monkeys.design.renderer.ontology-table",
  numericSeries: "monkeys.design.renderer.numeric-series",
  summaryChart: "monkeys.design.renderer.summary-chart",
  filePicker: "monkeys.design.renderer.file-picker",
  runtimeTerminal: "monkeys.design.renderer.runtime-terminal",
  orderedSelection: "monkeys.design.renderer.ordered-selection",
  ontologyTrendMatrix: "monkeys.design.renderer.ontology-trend-matrix",
  dataContextBar: "monkeys.design.renderer.data-context-bar",
  cardGrid: "monkeys.design.renderer.card-grid",
  gallery: "monkeys.design.renderer.gallery",
  pagination: "monkeys.design.renderer.pagination",
  recordDetail: "monkeys.design.renderer.record-detail",
  recordEditor: "monkeys.design.renderer.record-editor",
  recordHistory: "monkeys.design.renderer.record-history",
  ontologyManagement: "monkeys.design.renderer.ontology-management",
  featureColumnManager: "monkeys.design.renderer.feature-column-manager",
  sectionHeader: "monkeys.design.renderer.section-header",
  notice: "monkeys.design.renderer.notice",
  descriptionList: "monkeys.design.renderer.description-list",
  contentPanel: "monkeys.design.renderer.content-panel",
  toolbar: "monkeys.design.renderer.toolbar",
  status: "monkeys.design.renderer.status",
  professionalLeaf: "monkeys.design.renderer.professional-leaf",
  professionalExtensionLeaf: "monkeys.design.renderer.professional-extension-leaf",
} as const);

import { createDesignCapabilityEntry as createEntry, i18n, jsonSchema, type PropertyFieldDefinition, type OutputPortDefinition } from './entry';

const SIDEBAR_FILTER_SCHEMA =
  "monkeys-design://ontology-view/sidebar-filter/v1";
const governedSelectionValueSchema = (
  kind: "tags" | "files" | "relations",
): JsonObject => ({
  type: "object",
  additionalProperties: false,
  required: ["kind", "items"],
  properties: {
    kind: { const: kind },
    items: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "label"],
        properties: {
          id: { type: "string", minLength: 1 },
          label: { type: "string", minLength: 1 },
          labelI18n: I18N_DISPLAY_TEXT_SCHEMA,
          ...(kind === "tags"
            ? { color: { type: "string", minLength: 1 } }
            : {}),
          ...(kind === "files"
            ? { mediaType: { type: "string", minLength: 1 } }
            : {}),
          ...(kind === "relations"
            ? { ontologyId: { type: "string", minLength: 1 } }
            : {}),
        },
      },
    },
  },
});
const GOVERNED_JSON_VALUE_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  required: ["kind", "dataType", "value"],
  properties: {
    kind: { const: "json" },
    dataType: { type: "string", minLength: 1 },
    value: {},
  },
};
const GOVERNED_READ_VALUE_SCHEMA: JsonObject = {
  oneOf: [
    { type: ["string", "number", "boolean", "null"] },
    governedSelectionValueSchema("tags"),
    governedSelectionValueSchema("files"),
    governedSelectionValueSchema("relations"),
    GOVERNED_JSON_VALUE_SCHEMA,
  ],
};

const GOVERNED_READ_METADATA_SCHEMA: JsonObject = {
  type: "array",
  items: {
    type: "object",
    additionalProperties: false,
    required: ["id", "value"],
    properties: {
      id: { type: "string", minLength: 1 },
      label: { type: "string" },
      labelKey: { type: "string", minLength: 1 },
      value: GOVERNED_READ_VALUE_SCHEMA,
    },
  },
};

const GALLERY_SCHEMA = "monkeys-design://ontology-view/gallery/v4";
const PROFESSIONAL_SCHEMA =
  "monkeys-design://ontology-view/professional-leaf/v1";

const DATA_EXPLORER_APPEARANCE_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  properties: {
    surface: { enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.surface] },
    preset: {
      enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.preset],
    },
    density: { enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.density] },
    radius: { enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.radius] },
    border: { enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.border] },
    background: { enum: [...DATA_EXPLORER_APPEARANCE_OPTIONS.background] },
  },
};

const SHELL_LAYOUT_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/shell-layout/v3",
  {
    type: "object",
    additionalProperties: false,
    properties: {
      variant: { enum: ["default", "data-explorer", "region-stack"] },
      fullHeight: { type: "boolean" },
      sidebarLabelKey: { type: "string", minLength: 1 },
      mobileSidebar: { enum: ["stack", "drawer"] },
      asideLabelKey: { type: "string", minLength: 1 },
      sidebarCollapsed: { type: "boolean" },
      sidebarCollapsible: { type: "boolean" },
      sidebarResizable: { type: "boolean" },
      sidebarWidth: { type: "integer", minimum: 200, maximum: 600 },
      asideWidth: { type: "integer", minimum: 320, maximum: 560 },
      asideResizable: { type: "boolean" },
      appearance: DATA_EXPLORER_APPEARANCE_SCHEMA,
    },
  },
);

const SHELL_LAYOUT_INTENT_SCHEMAS = Object.freeze({
  sidebarCollapse: jsonSchema(
    "monkeys-design://intent/shell-layout-sidebar-collapse/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["collapsed"],
      properties: { collapsed: { type: "boolean" } },
    },
  ),
  sidebarResize: jsonSchema(
    "monkeys-design://intent/shell-layout-sidebar-resize/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["width"],
      properties: {
        width: { type: "integer", minimum: 200, maximum: 600 },
      },
    },
  ),
  asideResize: jsonSchema(
    "monkeys-design://intent/shell-layout-aside-resize/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["width"],
      properties: {
        width: { type: "integer", minimum: 320, maximum: 560 },
      },
    },
  ),
});

const SIDEBAR_FILTER_RENDER_MODEL_SCHEMA = jsonSchema(SIDEBAR_FILTER_SCHEMA, {
  type: "object",
  additionalProperties: false,
  properties: {
    title: { type: "string" },
    titleKey: { type: "string", minLength: 1 },
    search: {
      type: "object",
      additionalProperties: false,
      required: ["parameterName"],
      properties: {
        parameterName: { type: "string", minLength: 1 },
        value: { type: "string" },
        labelKey: { type: "string", minLength: 1 },
        placeholderKey: { type: "string", minLength: 1 },
      },
    },
    groups: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "parameterName", "options"],
        properties: {
          id: { type: "string", minLength: 1 },
          parameterName: { type: "string", minLength: 1 },
          label: { type: "string" },
          labelKey: { type: "string", minLength: 1 },
          options: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["id"],
              properties: {
                id: { type: "string", minLength: 1 },
                label: { type: "string" },
                labelKey: { type: "string", minLength: 1 },
                selected: { type: "boolean" },
                disabled: { type: "boolean" },
              },
            },
          },
        },
      },
    },
  },
});

const COLLECTION_ACTION_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  required: ["id"],
  properties: {
    id: { type: "string", minLength: 1 },
    label: { type: "string" },
    labelKey: { type: "string", minLength: 1 },
    icon: {
      type: "object",
      additionalProperties: false,
      required: ["id"],
      properties: {
        id: { type: "string", minLength: 1 },
        source: { enum: ["catalog", "system"] },
        labelKey: { type: "string", minLength: 1 },
      },
    },
    payload: { type: "object" },
    disabled: { type: "boolean" },
    loading: { type: "boolean" },
    destructive: { type: "boolean" },
    presentation: {
      enum: ["primary", "secondary", "menu", "danger", "overflow", "icon"],
    },
  },
};

const GOVERNED_VALUE_TYPE_SCHEMA: JsonObject = {
  enum: [
    "text",
    "number",
    "boolean",
    "timestamp",
    "json",
    "tags",
    "files",
    "relations",
    "media",
  ],
};

const GALLERY_FIELD_VALUES_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: { oneOf: [
    ...TABLE_DISPLAY_CELL_SCHEMAS,
    { type: "object", additionalProperties: false, required: ["reference"], properties: { reference: { type: "object", additionalProperties: false, required: ["id"], properties: { id: { type: "string", minLength: 1 } } }, alt: { type: "string" } } },
    GOVERNED_READ_VALUE_SCHEMA,
    { type: "object", additionalProperties: false, required: ["valueType", "value"], properties: {
      valueType: GOVERNED_VALUE_TYPE_SCHEMA, value: GOVERNED_READ_VALUE_SCHEMA,
      locale: { type: "string", minLength: 2, maxLength: 64 },
    } },
  ] },
};
const GALLERY_PRESENTATION_MODEL_PROPERTIES: JsonObject = {
  galleryDetailPresentation: { enum: ['tiles', 'lines'] },
  galleryActionPlacement: { enum: ['details', 'header'] },
  showFieldCount: { type: 'boolean' },
  galleryFields: GALLERY_PRESENTATION_FIELDS_SCHEMA,
  galleryLabels: { type: "object", additionalProperties: false, properties: { fieldCountKey: { type: "string", minLength: 1 }, coverOnlyHintKey: { type: "string", minLength: 1 } } },
  gallerySettings: GALLERY_PRESENTATION_SETTINGS_SCHEMA,
  itemActivation: { enum: ["none", "activate"] },
};

const GALLERY_RENDER_MODEL_SCHEMA = jsonSchema(GALLERY_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["items", "pagination"],
  properties: {
    ...GALLERY_PRESENTATION_MODEL_PROPERTIES,
    presentation: {
      type: "object",
      additionalProperties: false,
      required: ["kind"],
      properties: {
        kind: { const: "legacy-data-explorer" },
        loadingLabelKey: { type: "string", minLength: 1 },
        emptyLabelKey: { type: "string", minLength: 1 },
        errorLabelKey: { type: "string", minLength: 1 },
      },
    },
    items: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          context: { type: "object" },
          title: { type: "string" },
          titleKey: { type: "string", minLength: 1 },
          description: { type: "string" },
          descriptionKey: { type: "string", minLength: 1 },
          actionLabelKey: { type: "string", minLength: 1 },
          selected: { type: "boolean" },
          disabled: { type: "boolean" },
          metadata: GOVERNED_READ_METADATA_SCHEMA,
          fields: GALLERY_FIELD_VALUES_SCHEMA,
          leadingIcon: BASE_ASSET_ICON_DESCRIPTOR_SCHEMA,
          actionIds: { type: "array", uniqueItems: true, items: { type: "string", minLength: 1 } },
          actionAvailability: { type: "object", additionalProperties: { type: "object", additionalProperties: false, properties: { hidden: { type: "boolean" }, disabled: { type: "boolean" }, hintKey: { type: "string", minLength: 1 }, labelKey: { type: "string", minLength: 1 }, titleKey: { type: "string", minLength: 1 } } } },
          media: {
            type: "object",
            additionalProperties: false,
            required: ["reference", "alt"],
            properties: {
              reference: {
                type: "object",
                additionalProperties: false,
                required: ["id"],
                properties: {
                  id: { type: "string", minLength: 1 },
                },
              },
              alt: { type: "string", minLength: 1 },
              aspectRatio: { enum: ["1:1", "4:3", "3:4", "16:9", "9:16"] },
              placeholder: { enum: ["neutral", "checkerboard"] },
            },
          },
        },
      },
    },
    rowActions: { type: "array", items: COLLECTION_ACTION_SCHEMA },
    pagination: {
      type: "object",
      additionalProperties: false,
      required: ["hasMore", "nextCursor", "loadingMore"],
      properties: {
        hasMore: { type: "boolean" },
        nextCursor: {
          oneOf: [
            { type: "string", minLength: 1, maxLength: 2048 },
            { type: "null" },
          ],
        },
        loadingMore: { type: "boolean" },
        errorCode: { type: "string", minLength: 1, maxLength: 128 },
      },
      allOf: [
        {
          if: {
            properties: { hasMore: { const: true } },
            required: ["hasMore"],
          },
          then: {
            properties: { nextCursor: { type: "string", minLength: 1 } },
          },
        },
        {
          if: {
            properties: { hasMore: { const: false } },
            required: ["hasMore"],
          },
          then: {
            properties: {
              nextCursor: { type: "null" },
              loadingMore: { const: false },
            },
          },
        },
        {
          if: { required: ["errorCode"] },
          then: { properties: { loadingMore: { const: false } } },
        },
      ],
    },
  },
  allOf: [{ if: { required: ["galleryFields"] }, then: { required: ["gallerySettings"] } }, { if: { required: ["gallerySettings"] }, then: { required: ["galleryFields"] } }],
});

const localizedLabelProperties = {
  label: { type: "string" },
  labelKey: { type: "string", minLength: 1 },
} as const;

const GOVERNED_ICON_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  required: ["id"],
  properties: {
    id: { type: "string", minLength: 1 },
    source: { enum: ["catalog", "system"] },
    labelKey: { type: "string", minLength: 1 },
  },
};

const GOVERNED_ACTION_SCHEMA: JsonObject = {
  type: "object",
  additionalProperties: false,
  required: ["id"],
  properties: {
    id: { type: "string", minLength: 1 },
    ...localizedLabelProperties,
    icon: GOVERNED_ICON_SCHEMA,
    disabled: { type: "boolean" },
    loading: { type: "boolean" },
    destructive: { type: "boolean" },
    presentation: {
      enum: ["primary", "secondary", "menu", "danger", "overflow", "icon"],
    },
  },
};

const GOVERNED_ACTION_WITH_ARIA_SCHEMA: JsonObject = {
  ...GOVERNED_ACTION_SCHEMA,
  properties: {
    ...(GOVERNED_ACTION_SCHEMA.properties as JsonObject),
    ariaLabelKey: { type: "string", minLength: 1 },
  },
};


const itemIdIntentSchema = (id: string, field = "itemId") =>
  jsonSchema(id, {
    type: "object",
    additionalProperties: false,
    required: [field],
    properties: { [field]: { type: "string", minLength: 1 } },
  });

const cursorIntentSchema = (id: string) =>
  jsonSchema(id, {
    type: "object",
    additionalProperties: false,
    required: ["cursor"],
    properties: { cursor: { type: "string", minLength: 1, maxLength: 2048 } },
  });

const ONTOLOGY_NAVIGATOR_SCHEMA =
  "monkeys-design://ontology-view/ontology-navigator/v1";
const ONTOLOGY_NAVIGATOR_RENDER_MODEL_SCHEMA = jsonSchema(
  ONTOLOGY_NAVIGATOR_SCHEMA,
  {
    type: "object",
    additionalProperties: false,
    required: ["nodes", "pagination"],
    properties: {
      ...localizedLabelProperties,
      actionsLabelKey: { type: "string", minLength: 1 },
      search: {
        type: "object",
        additionalProperties: false,
        required: ["value"],
        properties: {
          value: { type: "string" },
          labelKey: { type: "string", minLength: 1 },
          placeholderKey: { type: "string", minLength: 1 },
          disabled: { type: "boolean" },
        },
      },
      loading: { type: "boolean" },
      error: {
        type: "object",
        additionalProperties: false,
        required: ["code"],
        properties: {
          code: { type: "string", minLength: 1, maxLength: 128 },
          messageKey: { type: "string", minLength: 1 },
          retryable: { type: "boolean" },
        },
      },
      retry: {
        type: "object",
        additionalProperties: false,
        required: ["labelKey"],
        properties: {
          labelKey: { type: "string", minLength: 1 },
          disabled: { type: "boolean" },
          loading: { type: "boolean" },
        },
      },
      selectedNodeId: { type: "string", minLength: 1 },
      expandedNodeIds: {
        type: "array",
        uniqueItems: true,
        items: { type: "string", minLength: 1 },
      },
      nodes: { type: "array", items: { $ref: "#/$defs/node" } },
      pagination: cursorPaginationSchema(),
    },
    $defs: {
      node: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          kind: {
            enum: [
              "ontology",
              "view",
              "folder",
              "group",
              "dashboard",
              "plugin",
              "action",
            ],
          },
          icon: GOVERNED_ICON_SCHEMA,
          description: { type: "string" },
          descriptionKey: { type: "string", minLength: 1 },
          meta: { type: "string" },
          disabled: { type: "boolean" },
          reorderable: { type: "boolean" },
          actions: { type: "array", items: GOVERNED_ACTION_SCHEMA },
          children: { type: "array", items: { $ref: "#/$defs/node" } },
        },
      },
    },
  },
);

const DATA_TOOLBAR_SCHEMA = "monkeys-design://render-model/data-toolbar/v6";
const DATA_TOOLBAR_RENDER_MODEL_SCHEMA = jsonSchema(DATA_TOOLBAR_SCHEMA, {
  type: "object",
  additionalProperties: false,
  properties: {
    appearance: DATA_EXPLORER_APPEARANCE_SCHEMA,
    presentation: {
      oneOf: [
        {
          type: "object",
          additionalProperties: false,
          required: ["kind", "title", "contract", "schemaRef", "readOnly"],
          properties: {
            kind: { const: "domain-record-equivalent" },
            title: { type: "string", minLength: 1 },
            contract: { type: "string", minLength: 1 },
            schemaRef: { type: "string", minLength: 1 },
            readOnly: { type: "boolean" },
          },
        },
        {
          type: "object",
          additionalProperties: false,
          required: ["kind", "scopeSelected", "auxiliaryActions"],
          properties: {
            kind: { const: "legacy-data-explorer" },
            scopeSelected: { type: "boolean" },
            auxiliaryActions: {
              type: "array",
              items: {
                oneOf: [
                  GOVERNED_ACTION_WITH_ARIA_SCHEMA,
                  {
                    type: "object",
                    additionalProperties: false,
                    required: [
                      "id",
                      "kind",
                      "labelKey",
                      "preset",
                      "mode",
                      "scale",
                      "options",
                    ],
                    properties: {
                      id: { type: "string", minLength: 1 },
                      kind: { const: "row-height" },
                      labelKey: { type: "string", minLength: 1 },
                      icon: GOVERNED_ICON_SCHEMA,
                      disabled: { type: "boolean" },
                      preset: { enum: ["compact", "default", "comfortable"] },
                      mode: { enum: ["preset", "custom"] },
                      scale: {
                        type: "number",
                        minimum: 0.5,
                        maximum: 10,
                        multipleOf: 0.5,
                      },
                      customLabelKey: { type: "string", minLength: 1 },
                      customRangeLabelKey: { type: "string", minLength: 1 },
                      options: {
                        type: "array",
                        minItems: 3,
                        maxItems: 3,
                        items: {
                          type: "object",
                          additionalProperties: false,
                          required: ["id", "labelKey"],
                          properties: {
                            id: { enum: ["compact", "default", "comfortable"] },
                            labelKey: { type: "string", minLength: 1 },
                          },
                        },
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      ],
    },
    search: {
      type: "object",
      additionalProperties: false,
      required: ["value"],
      properties: {
        value: { type: "string" },
        labelKey: { type: "string", minLength: 1 },
        placeholderKey: { type: "string", minLength: 1 },
      },
    },
    activeViewId: { type: "string", minLength: 1 },
    views: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          disabled: { type: "boolean" },
        },
      },
    },
    refresh: {
      type: "object",
      additionalProperties: false,
      required: ["labelKey"],
      properties: {
        labelKey: { type: "string", minLength: 1 },
        disabled: { type: "boolean" },
        loading: { type: "boolean" },
      },
    },
    commands: {
      type: "array",
      items: GOVERNED_ACTION_WITH_ARIA_SCHEMA,
    },
    selection: {
      type: "object",
      additionalProperties: false,
      required: ["selectedCount"],
      properties: {
        selectedCount: { type: "integer", minimum: 0 },
        totalCount: { type: "integer", minimum: 0 },
        allSelected: { type: "boolean" },
        labelKey: { type: "string", minLength: 1 },
      },
    },
    filterGroups: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "kind", "values"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          kind: { enum: ["single", "multiple", "range"] },
          values: {
            type: "array",
            uniqueItems: true,
            items: GOVERNED_READ_VALUE_SCHEMA,
          },
          options: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["id"],
              properties: {
                id: { type: "string", minLength: 1 },
                ...localizedLabelProperties,
                icon: GOVERNED_ICON_SCHEMA,
                disabled: { type: "boolean" },
                count: { type: "integer", minimum: 0 },
              },
            },
          },
          disabled: { type: "boolean" },
          loading: { type: "boolean" },
          errorCode: { type: "string", minLength: 1, maxLength: 128 },
        },
      },
    },
    displayActions: {
      type: "array",
      items: GOVERNED_ACTION_WITH_ARIA_SCHEMA,
    },
    galleryFields: GALLERY_PRESENTATION_FIELDS_SCHEMA,
    gallerySettings: { oneOf: [GALLERY_PRESENTATION_SETTINGS_SCHEMA, {
      type: "object",
      additionalProperties: false,
      properties: {
        columns: { oneOf: [{ const: "auto" }, { type: "integer", minimum: 1, maximum: 12 }] },
        aspectRatio: { enum: ["auto", "1:1", "4:3", "3:4", "16:9", "9:16"] },
        showMetadata: { type: "boolean" },
        actions: { type: "array", items: GOVERNED_ACTION_WITH_ARIA_SCHEMA },
      },
    }] },
    columnConfiguration: {
      type: "object",
      additionalProperties: false,
      required: [
        "disabled",
        "triggerLabelKey",
        "titleKey",
        "descriptionKey",
        "closeLabelKey",
        "expectedDefinitionVersion",
        "displayConfig",
        "sections",
      ],
      properties: {
        disabled: { type: "boolean" },
        triggerLabelKey: { type: "string", minLength: 1 },
        titleKey: { type: "string", minLength: 1 },
        descriptionKey: { type: "string", minLength: 1 },
        closeLabelKey: { type: "string", minLength: 1 },
        expectedDefinitionVersion: { type: "integer", minimum: 1 },
        displayConfig: { type: "object", additionalProperties: true },
        sections: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["id", "labelKey", "selectedIds", "options"],
            properties: {
              id: { type: "string", minLength: 1, maxLength: 128 },
              labelKey: { type: "string", minLength: 1 },
              searchable: { type: "boolean" },
              searchPlaceholderKey: { type: "string", minLength: 1 },
              emptyLabelKey: { type: "string", minLength: 1 },
              selectedIds: {
                type: "array",
                uniqueItems: true,
                items: { type: "string", minLength: 1, maxLength: 256 },
              },
              options: {
                type: "array",
                items: {
                  type: "object",
                  additionalProperties: false,
                  required: ["id"],
                  properties: {
                    id: { type: "string", minLength: 1, maxLength: 256 },
                    ...localizedLabelProperties,
                    description: { type: "string" },
                    descriptionKey: { type: "string", minLength: 1 },
                    icon: GOVERNED_ICON_SCHEMA,
                    disabled: { type: "boolean" },
                  },
                },
              },
            },
          },
        },
      },
    },
    trendMatrixConfiguration: {
      type: "object",
      additionalProperties: false,
      required: [
        "disabled",
        "triggerLabelKey",
        "titleKey",
        "descriptionKey",
        "sourceFieldLabelKey",
        "generatedFieldLabelKey",
        "showHistoryLabelKey",
        "showHistoryHintKey",
        "expectedDefinitionVersion",
        "displayConfig",
        "value",
        "fieldOptions",
      ],
      properties: {
        disabled: { type: "boolean" },
        triggerLabelKey: { type: "string", minLength: 1 },
        titleKey: { type: "string", minLength: 1 },
        descriptionKey: { type: "string", minLength: 1 },
        sourceFieldLabelKey: { type: "string", minLength: 1 },
        generatedFieldLabelKey: { type: "string", minLength: 1 },
        showHistoryLabelKey: { type: "string", minLength: 1 },
        showHistoryHintKey: { type: "string", minLength: 1 },
        expectedDefinitionVersion: { type: "integer", minimum: 1 },
        displayConfig: { type: "object", additionalProperties: true },
        value: {
          type: "object",
          additionalProperties: false,
          required: ["sourceFieldKey", "generatedFieldKey", "historyMode"],
          properties: {
            sourceFieldKey: { type: "string", minLength: 1 },
            generatedFieldKey: { type: "string" },
            historyMode: { enum: ["current", "all"] },
          },
        },
        fieldOptions: {
          type: "array",
          minItems: 1,
          items: {
            type: "object",
            additionalProperties: false,
            required: ["id", "kind"],
            properties: {
              id: { type: "string", minLength: 1, maxLength: 256 },
              ...localizedLabelProperties,
              kind: { enum: ["preview", "featureImage"] },
            },
          },
        },
      },
    },
  },
});

const ONTOLOGY_TABLE_SCHEMA = "monkeys-design://ontology-view/table/v5";
const ONTOLOGY_TABLE_RENDER_MODEL_SCHEMA = jsonSchema(ONTOLOGY_TABLE_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["columns", "rows", "pagination"],
  properties: {
    tableMinWidth: { type: 'number', minimum: 0, maximum: 10000 },
    showFooter: { type: 'boolean' },
    actionWidth: { type: 'number', minimum: 0, maximum: 10000 },
    initialSelectionId: { type: "string", minLength: 1, maxLength: 512 },
    ...localizedLabelProperties,
    emptyLabelKey: { type: "string", minLength: 1 },
    selectionEnabled: { type: "boolean" },
    activationEnabled: { type: "boolean" },
    presentation: {
      type: "object",
      additionalProperties: false,
      required: ["kind"],
      properties: {
        kind: { enum: ["legacy-data-explorer", "domain-record-equivalent"] },
        loadingLabelKey: { type: "string", minLength: 1 },
        emptyLabelKey: { type: "string", minLength: 1 },
        errorLabelKey: { type: "string", minLength: 1 },
      },
    },
    rowHeight: {
      type: "object",
      additionalProperties: false,
      required: ["mode", "preset", "scale"],
      properties: {
        mode: { enum: ["preset", "custom"] },
        preset: { enum: ["compact", "default", "comfortable"] },
        scale: { type: "number", minimum: 0.5, maximum: 10, multipleOf: 0.5 },
      },
    },
    columns: {
      type: "array",
      minItems: 1,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          sortable: { type: "boolean" },
          sortKey: {
            enum: ["asset_id", "updated_timestamp", "pin_order"],
          },
          sortDirection: { enum: ["asc", "desc", "none"] },
          align: { enum: ["left", "center", "right"] },
          width: { type: "number", minimum: 0, maximum: 10000 }, minWidth: { type: "number", minimum: 0, maximum: 10000 }, wrap: { type: "boolean" },
          valueType: GOVERNED_VALUE_TYPE_SCHEMA,
          hidden: { type: "boolean" },
          actions: { type: "array", items: GOVERNED_ACTION_SCHEMA },
        },
        allOf: [
          {
            if: {
              properties: { sortable: { const: true } },
              required: ["sortable"],
            },
            then: { required: ["sortKey"] },
          },
        ],
      },
    },
    rows: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "cells"],
        properties: {
          id: { type: "string", minLength: 1 },
          context: { type: "object" },
          defaultSelected: { type: "boolean" },
          cells: {
            type: "object",
            additionalProperties: {
              oneOf: [
                ...TABLE_DISPLAY_CELL_SCHEMAS,
                { type: 'object', additionalProperties: false, required: ['valueType', 'value'], properties: {
                  valueType: { const: 'message' }, value: TABLE_MESSAGE_VALUE_SCHEMA,
                  editable: { type: 'boolean' }, intentId: { type: 'string', minLength: 1 }, disabled: { type: 'boolean' },
                } },
                GOVERNED_READ_VALUE_SCHEMA,
                {
                  type: "object",
                  additionalProperties: false,
                  required: ["valueType", "value"],
                  properties: {
                    locale: { type: 'string', minLength: 2, maxLength: 64 },
                    valueType: GOVERNED_VALUE_TYPE_SCHEMA,
                    value: GOVERNED_READ_VALUE_SCHEMA,
                    editable: { type: "boolean" },
                    intentId: { type: "string", minLength: 1 },
                    disabled: { type: "boolean" },
                  },
                },
              ],
            },
          },
          actionAvailability: {
            type: "object",
            propertyNames: { minLength: 1 },
            additionalProperties: {
              type: "object",
              additionalProperties: false,
              properties: {
                hidden: { type: "boolean" },
                disabled: { type: "boolean" },
                labelKey: { type: "string", minLength: 1 }, titleKey: { type: "string", minLength: 1 },
              },
            },
          },
          selected: { type: "boolean" },
          disabled: { type: "boolean" },
        },
      },
    },
    selectAll: {
      type: "object",
      additionalProperties: false,
      required: ["selected", "indeterminate"],
      properties: {
        selected: { type: "boolean" },
        indeterminate: { type: "boolean" },
        disabled: { type: "boolean" },
        labelKey: { type: "string", minLength: 1 },
      },
    },
    rowActions: { type: "array", items: COLLECTION_ACTION_SCHEMA },
    columnActions: { type: "array", items: GOVERNED_ACTION_SCHEMA },
    pagination: cursorPaginationSchema(),
  },
});

const CARD_GRID_SCHEMA = "monkeys-design://ontology-view/card-grid/v2";
const CARD_GRID_RENDER_MODEL_SCHEMA = jsonSchema(CARD_GRID_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["items", "pagination"],
  properties: {
    ...GALLERY_PRESENTATION_MODEL_PROPERTIES,
    presentation: {
      type: "object",
      additionalProperties: false,
      required: ["kind"],
      properties: {
        kind: { const: "legacy-data-explorer" },
        loadingLabelKey: { type: "string", minLength: 1 },
        emptyLabelKey: { type: "string", minLength: 1 },
        errorLabelKey: { type: "string", minLength: 1 },
      },
    },
    items: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          title: { type: "string" },
          titleKey: { type: "string", minLength: 1 },
          description: { type: "string" },
          descriptionKey: { type: "string", minLength: 1 },
          selected: { type: "boolean" },
          disabled: { type: "boolean" },
          metadata: GOVERNED_READ_METADATA_SCHEMA,
          fields: GALLERY_FIELD_VALUES_SCHEMA,
          leadingIcon: BASE_ASSET_ICON_DESCRIPTOR_SCHEMA,
          actionIds: { type: "array", uniqueItems: true, items: { type: "string", minLength: 1 } },
          actionAvailability: { type: "object", additionalProperties: { type: "object", additionalProperties: false, properties: { hidden: { type: "boolean" }, disabled: { type: "boolean" }, hintKey: { type: "string", minLength: 1 }, labelKey: { type: "string", minLength: 1 }, titleKey: { type: "string", minLength: 1 } } } },
        },
      },
    },
    rowActions: { type: "array", items: GOVERNED_ACTION_SCHEMA },
    pagination: cursorPaginationSchema(),
  },
  allOf: [{ if: { required: ["galleryFields"] }, then: { required: ["gallerySettings"] } }, { if: { required: ["gallerySettings"] }, then: { required: ["galleryFields"] } }],
});

const PAGINATION_SCHEMA = "monkeys-design://render-model/pagination/v1";
const PAGINATION_RENDER_MODEL_SCHEMA = jsonSchema(PAGINATION_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["mode", "hasNext", "hasPrevious"],
  properties: {
    mode: { enum: ["pages", "cursor"] },
    page: { type: "integer", minimum: 1 },
    pageSize: { type: "integer", minimum: 1 },
    total: { type: "integer", minimum: 0 },
    pageCount: { type: "integer", minimum: 0 },
    hasNext: { type: "boolean" },
    hasPrevious: { type: "boolean" },
    nextCursor: { oneOf: [{ type: "string", minLength: 1 }, { type: "null" }] },
    loading: { type: "boolean" },
    errorCode: { type: "string", minLength: 1 },
    labelKey: { type: "string", minLength: 1 },
  },
});

const RECORD_DETAIL_SCHEMA = "monkeys-design://ontology-view/record-detail/v4";
const RECORD_DETAIL_RENDER_MODEL_SCHEMA = jsonSchema(RECORD_DETAIL_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["fields"],
  properties: {
    presentation: {
      type: "object",
      additionalProperties: false,
      required: ["kind"],
      properties: {
        kind: { enum: ["hidden", "data-explorer-sidebar"] },
      },
    },
    dataExplorerRecord: {
      type: "object",
      additionalProperties: false,
      required: ["id", "title", "viewCount", "downloadCount", "tagLabels"],
      properties: {
        id: { type: "string", minLength: 1, maxLength: 256 },
        title: { type: "string" },
        description: { type: "string" },
        type: { type: "string" },
        status: { type: "string" },
        previewSource: { type: "string" },
        previewAlt: { type: "string" },
        previewKind: {
          enum: ["image", "video", "audio", "document", "file", "text"],
        },
        viewCount: { type: "integer", minimum: 0 },
        downloadCount: { type: "integer", minimum: 0 },
        tagLabels: { type: "array", items: { type: "string", minLength: 1 } },
        createdAt: { type: ["string", "number"] },
        updatedAt: { type: ["string", "number"] },
        creatorId: { type: "string" },
        properties: {
          type: "object",
          additionalProperties: GOVERNED_READ_VALUE_SCHEMA,
        },
        metadata: {
          type: "object",
          additionalProperties: true,
        },
      },
    },
    title: { type: "string" },
    titleKey: { type: "string", minLength: 1 },
    description: { type: "string" },
    descriptionKey: { type: "string", minLength: 1 },
    editable: { type: "boolean" },
    editDisabled: { type: "boolean" },
    editLabelKey: { type: "string", minLength: 1 },
    editAriaLabelKey: { type: "string", minLength: 1 },
    closeLabelKey: { type: "string", minLength: 1 },
    closeAriaLabelKey: { type: "string", minLength: 1 },
    closeEnabled: { type: "boolean" },
    fields: {
      type: "array",
      items: { $ref: "#/$defs/field" },
    },
    sections: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "fields"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          description: { type: "string" },
          descriptionKey: { type: "string", minLength: 1 },
          collapsible: { type: "boolean" },
          collapsed: { type: "boolean" },
          fields: { type: "array", items: { $ref: "#/$defs/field" } },
        },
      },
    },
    preview: {
      type: "object",
      additionalProperties: false,
      required: ["kind", "reference"],
      properties: {
        kind: { enum: ["image", "video", "audio", "document", "file"] },
        reference: {
          type: "object",
          additionalProperties: false,
          required: ["id"],
          properties: {
            id: { type: "string", minLength: 1 },
            label: { type: "string" },
            mediaType: { type: "string", minLength: 1 },
          },
        },
        alt: { type: "string" },
        altKey: { type: "string", minLength: 1 },
      },
    },
    attachments: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: {
          id: { type: "string", minLength: 1 },
          ...localizedLabelProperties,
          mediaType: { type: "string", minLength: 1 },
          sizeBytes: { type: "integer", minimum: 0 },
          actionId: { type: "string", minLength: 1 },
          disabled: { type: "boolean" },
        },
      },
    },
    navigation: {
      type: "object",
      additionalProperties: false,
      properties: {
        previousId: { type: "string", minLength: 1 },
        nextId: { type: "string", minLength: 1 },
        previousLabelKey: { type: "string", minLength: 1 },
        nextLabelKey: { type: "string", minLength: 1 },
      },
    },
    actions: { type: "array", items: GOVERNED_ACTION_WITH_ARIA_SCHEMA },
  },
  $defs: {
    field: {
      type: "object",
      additionalProperties: false,
      required: ["id"],
      properties: {
        id: { type: "string", minLength: 1 },
        ...localizedLabelProperties,
        value: GOVERNED_READ_VALUE_SCHEMA,
        locale: { type: 'string', minLength: 2, maxLength: 64 },
        valueType: GOVERNED_VALUE_TYPE_SCHEMA,
        valueLabelKey: { type: "string", minLength: 1 },
        actionId: { type: "string", minLength: 1 },
        copy: {type:"object",additionalProperties:false,required:["labelKey","successLabelKey","failureLabelKey"],properties:{labelKey:{type:"string",minLength:1},successLabelKey:{type:"string",minLength:1},failureLabelKey:{type:"string",minLength:1}}},
      },
    },
  },
});

const RECORD_EDITOR_SCHEMA = "monkeys-design://ontology-editor/record/v1";
const editorPathSchema: JsonObject = { type: 'array', maxItems: 32, items: { oneOf: [{ type: 'string', minLength: 1 }, { type: 'object', additionalProperties: false, required: ['itemId', 'idKey'], properties: { itemId: { type: 'string', minLength: 1 }, idKey: { type: 'string', minLength: 1 } } }] } };
const editorChangeEffectsSchema: JsonObject = {
  type: 'array', maxItems: 32,
  items: { oneOf: [
    { type: 'object', additionalProperties: false, required: ['path', 'value'], properties: { path: editorPathSchema, pathScope: { enum: ['root', 'parent'] }, value: {}, when: { type: 'object' } } },
    { type: 'object', additionalProperties: false, required: ['path', 'expression'], properties: { path: editorPathSchema, pathScope: { enum: ['root', 'parent'] }, expression: { type: 'object' }, when: { type: 'object' } } },
  ] },
};
const RECORD_EDITOR_DESCRIPTION_EXCLUSIVITY: JsonObject[] = [['description', 'descriptionKey'], ['description', 'descriptionI18n'], ['descriptionKey', 'descriptionI18n']].map(required => ({ not: { required } }));
const RECORD_EDITOR_COMMON_FIELD_PROPERTIES = {
  description: { type: 'string' },
  descriptionKey: { type: 'string', minLength: 1 },
  descriptionI18n: I18N_DISPLAY_TEXT_SCHEMA,
  path: editorPathSchema,
  changeEffects: editorChangeEffectsSchema,
  hasValue: { type: 'boolean' },
  requiredWhen: { type: 'object' },
  disabledWhen: { type: 'object' },
  hiddenWhen: { type: 'object' },
  hasValueWhen: { type: 'object' },
  itemIdKey: { type: 'string', minLength: 1 },
  itemDefault: { type: 'object' },
  children: { type: 'array', items: { $ref: RECORD_EDITOR_SCHEMA + '#/properties/fields/items' } },
  id: { type: "string", minLength: 1 },
  ...localizedLabelProperties,
  labelI18n: I18N_DISPLAY_TEXT_SCHEMA,
  labelParameters: LOCALIZED_DISPLAY_PARAMETERS_SCHEMA,
  required: { type: "boolean" },
  disabled: { type: "boolean" },
  hidden: { type: "boolean" },
  errorCode: { type: "string", minLength: 1 },
  placeholder: { type: "string" },
  placeholderKey: { type: "string", minLength: 1 },
  fallbackValue: { type: "string" },
  columnSpan: { type: "integer", minimum: 1, maximum: 3 },
  rows: { type: "integer", minimum: 1, maximum: 20 },
  multiline: { type: "boolean" },
} as const;
const editorNullToggleSchema = (retain: boolean): JsonObject => ({ type: 'object', additionalProperties: false, required: ['labelKey'], properties: { labelKey: { type: 'string', minLength: 1 }, checkedWhen: { enum: ['null', 'non-null'] }, retainSelectionOnNull: retain ? { type: 'boolean' } : { const: false } } });
const EDITOR_STRING_SELECTION_PROPERTIES = { searchable: { type: 'boolean' }, allowCustomValues: { type: 'boolean' }, showSelectedIds: { type: 'boolean' } };
const RECORD_EDITOR_ARRAY_BOUNDS = { addDisabled: { type: 'boolean' }, addLabelKey: { type: 'string', minLength: 1 }, removeLabelKey: { type: 'string', minLength: 1 }, reorderable: { type: 'boolean' }, minItems: { type: 'integer', minimum: 0, maximum: 1000 }, maxItems: { type: 'integer', minimum: 0, maximum: 1000 } } as const;
const recordEditorFieldSchema = (
  kinds: readonly string[],
  value: JsonObject,
  selectable = false,
): JsonObject => ({
  type: "object",
  additionalProperties: false,
  required: ["id", "kind"],
  allOf: [...RECORD_EDITOR_DESCRIPTION_EXCLUSIVITY,
    ...(kinds.includes('multi-select') ? [{ if: { required: ['nullToggle'] }, then: {}, else: { properties: { value: { type: 'array' } } } }] : []),
    { not: { required: ['options', 'optionsSource'] } },
  ],
  properties: {
    ...RECORD_EDITOR_COMMON_FIELD_PROPERTIES,
    kind: { enum: [...kinds] },
    ...(kinds.includes('array') ? { ...RECORD_EDITOR_ARRAY_BOUNDS, nullToggle: editorNullToggleSchema(false) } : {}),
    ...(kinds.includes('multi-select') ? { ...EDITOR_STRING_SELECTION_PROPERTIES, nullToggle: editorNullToggleSchema(true), optionsSource: RECORD_EDITOR_OPTIONS_SOURCE_SCHEMA } : {}),
    ...(kinds.includes('select') ? { optionsSource: RECORD_EDITOR_OPTIONS_SOURCE_SCHEMA } : {}),
    ...(kinds.includes('secret') ? { inputKind: { enum: ['password', 'text', 'textarea', 'boolean', 'number', 'json'] } } : {}),
    value,
    ...(kinds.includes('relation') ? { presentation: { enum: ['inline', 'picker'] }, pickLabelKey: { type: 'string', minLength: 1 } } : {}),
    ...(selectable
      ? {
          options: { type: "array", items: kinds.some(kind => kind === 'text' || kind === 'select' || kind === 'multi-select') ? { ...RECORD_EDITOR_OPTION_SCHEMA, properties: { ...RECORD_EDITOR_OPTION_SCHEMA.properties as JsonObject, id: { type: 'string' } } } : RECORD_EDITOR_OPTION_SCHEMA },
          optionsPagination: cursorPaginationSchema(),
        }
      : {}),
  },
});
const editorDraftPatchSchema: JsonObject = {
  type: 'object', additionalProperties: false, required: ['key', 'revision', 'expectedValue', 'operations'], properties: {
    key: { type: 'string', minLength: 1, maxLength: 128 }, revision: { type: 'string', minLength: 1, maxLength: 256 }, expectedValue: { type: 'object' },
    operations: { type: 'array', minItems: 1, maxItems: 32, items: { oneOf: [
      { type: 'object', additionalProperties: false, required: ['kind', 'path', 'value'], properties: { kind: { const: 'set' }, path: editorPathSchema, value: {} } },
      { type: 'object', additionalProperties: false, required: ['kind', 'path'], properties: { kind: { const: 'touch' }, path: editorPathSchema } },
      ...['insert', 'remove', 'move'].map(kind => ({ type: 'object', additionalProperties: false, required: ['kind', 'path', kind === 'insert' ? 'value' : 'itemId'], properties: {
        kind: { const: kind }, path: editorPathSchema, idKey: { type: 'string', minLength: 1 },
        ...(kind === 'insert' ? { value: { type: 'object' } } : { itemId: { type: 'string', minLength: 1 } }),
        ...(kind !== 'remove' ? { beforeId: { type: 'string', minLength: 1 } } : {}),
      } })),
    ] } },
  },
};
const RECORD_EDITOR_RENDER_MODEL_SCHEMA = jsonSchema(RECORD_EDITOR_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: ["fields", "submitting"],
  not: { required: ['description', 'descriptionKey'] },
  properties: {
    readOnly: { type: 'boolean' },
    resetOnCancel: { type: 'boolean' },
    lookup: { type: 'object' },
    optionTables: RECORD_EDITOR_OPTION_TABLES_SCHEMA,
    draftPatch: { anyOf: [{ type: 'null' }, editorDraftPatchSchema] },
    draft: { type: 'object', additionalProperties: false, required: ['revision', 'value'], properties: { revision: { type: 'string', minLength: 1 }, value: { type: 'object' } } },
    draftValidation: { type: 'object', additionalProperties: false, required: ['revision', 'value', 'errors'], properties: { revision: { type: 'string', minLength: 1 }, value: { type: 'object' }, errors: { type: 'array', items: { type: 'object', additionalProperties: false, required: ['path', 'code'], properties: { path: editorPathSchema, code: { type: 'string', minLength: 1 } } } } } },
    title: { type: "string" },
    titleKey: { type: "string", minLength: 1 },
    recordTitle: { type: "string" },
    secretInputScopeKey: { type: "string", minLength: 1 },
    description: { type: 'string' },
    descriptionKey: { type: "string", minLength: 1 },
    descriptionParameters: LOCALIZED_DISPLAY_PARAMETERS_SCHEMA,
    presentation: { enum: ["panel", "dialog", "data-explorer-dialog", "toolbar", "sections"] },
    initialFocus: { enum: ["dialog", "first-field"] },
    layoutColumns: { type: "integer", minimum: 1, maximum: 3 },
    auxiliarySubmissions: { type: "array", maxItems: 16, items: { type: "object", additionalProperties: false, required: ["id", "labelKey", "validateFieldIds"], properties: {
      id: { type: "string", minLength: 1, maxLength: 256 }, labelKey: { type: "string", minLength: 1 }, disabled: { type: "boolean" },
      validateFieldIds: { type: "array", minItems: 1, uniqueItems: true, items: { type: "string", minLength: 1, maxLength: 256 } },
    } } },
    fieldGroups: {
      type: 'array', maxItems: 100,
      items: { type: 'object', additionalProperties: false, required: ['id', 'titleKey', 'fieldIds'], properties: {
        id: { type: 'string', minLength: 1 }, titleKey: { type: 'string', minLength: 1 }, descriptionKey: { type: 'string', minLength: 1 },
        fieldIds: { type: 'array', uniqueItems: true, items: { type: 'string', minLength: 1 } }, columns: { enum: [1, 2, 3] }, surface: { enum: ['plain', 'panel'] },
      } },
    },
    fieldRows: {
      type: "array",
      items: {
        type: "array",
        minItems: 1,
        maxItems: 3,
        uniqueItems: true,
        items: { type: "string", minLength: 1 },
      },
    },
    visibleFieldIds: {
      type: "array",
      uniqueItems: true,
      items: { type: "string", minLength: 1 },
    },
    fieldPresentation: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        allOf: [...RECORD_EDITOR_DESCRIPTION_EXCLUSIVITY,
          ...[
            { kinds: ['multi-select'], keys: ['searchable', 'allowCustomValues', 'showSelectedIds'] },
            { kinds: ['multi-select', 'array'], keys: ['nullToggle'] },
            { kinds: ['multi-select', 'select'], keys: ['optionsSource'] },
          ].map(({ kinds, keys }) => ({ if: { required: ['kind'], properties: { kind: { not: { enum: kinds } } } }, then: { not: { anyOf: keys.map(key => ({ required: [key] })) } } })),
          { not: { required: ['options', 'optionsSource'] } }, { if: { required: ['kind'], properties: { kind: { const: 'array' } } }, then: { properties: { nullToggle: editorNullToggleSchema(false) } } }, { if: { required: ['kind'], properties: { kind: { not: { const: 'array' } } } }, then: { not: { anyOf: [{ required: ['minItems'] }, { required: ['maxItems'] }, { required: ['reorderable'] }, { required: ['addLabelKey'] }, { required: ['removeLabelKey'] }, { required: ['addDisabled'] }] } } }, { if: { required: ['kind'], properties: { kind: { enum: ['text', 'select', 'multi-select'] } } }, then: {}, else: { properties: { options: { type: 'array', items: RECORD_EDITOR_OPTION_SCHEMA } } } }],
        properties: {
          ...EDITOR_STRING_SELECTION_PROPERTIES, nullToggle: editorNullToggleSchema(true), optionsSource: RECORD_EDITOR_OPTIONS_SOURCE_SCHEMA,
          ...RECORD_EDITOR_COMMON_FIELD_PROPERTIES,
          ...RECORD_EDITOR_ARRAY_BOUNDS,
          kind: {
            enum: [
              "array",
              "date",
              "datetime-local",
              "text",
              "number",
              "boolean",
              "textarea",
              "secret",
              "select",
              "multi-select",
              "tags",
              "files",
              "media",
              "relation",
              "json",
            ],
          },
          valueFormat: { enum: ['date', 'iso-start-of-day', 'iso-end-of-day', 'local-datetime', 'iso'] },
          min: { type: 'string' }, max: { type: 'string' },
          inputKind: { enum: ['password', 'text', 'textarea', 'boolean', 'number', 'json'] },
          presentation: { enum: ['inline', 'picker'] }, pickLabelKey: { type: 'string', minLength: 1 },
          options: { type: 'array', items: { ...RECORD_EDITOR_OPTION_SCHEMA, properties: { ...RECORD_EDITOR_OPTION_SCHEMA.properties as JsonObject, id: { type: 'string' } } } },
        },
      },
    },
    submitDestructive: { type: "boolean" },
    showSubmit: { type: "boolean" },
    submitDisabled: { type: "boolean" },
    showCancel: { type: "boolean" },
    showReset: { type: "boolean" },
    submitLabelKey: { type: "string", minLength: 1 },
    submittingLabelKey: { type: "string", minLength: 1 },
    cancelLabelKey: { type: "string", minLength: 1 },
    submitting: { type: "boolean" },
    errorCode: { type: "string", minLength: 1 },
    fields: {
      type: "array",
      items: {
        oneOf: [
          recordEditorFieldSchema(["object"], { type: ["object", "null"] }),
          recordEditorFieldSchema(["array"], { type: ["array", "null"], items: { type: "object" } }),
          recordEditorFieldSchema(["secret"], { type: "null" }),
          recordEditorFieldSchema(['text'], { type: ['string', 'null'] }, true),
          recordEditorFieldSchema(['textarea'], { type: ['string', 'null'] }),
          recordEditorFieldSchema(
            ["select"],
            { type: ["string", "null"] },
            true,
          ),
          recordEditorFieldSchema(['multi-select'], { type: ['array', 'null'], uniqueItems: true, items: { type: 'string' } }, true),
          ...(['date', 'datetime-local'] as const).map(kind => {
            const schema = recordEditorFieldSchema([kind], { type: ['string', 'null'] });
            return { ...schema, properties: { ...schema.properties as JsonObject,
              valueFormat: { enum: kind === 'date' ? ['date', 'iso-start-of-day', 'iso-end-of-day'] : ['local-datetime', 'iso'] },
              min: { type: 'string' }, max: { type: 'string' },
            } };
          }),
          recordEditorFieldSchema(["number"], {
            type: ["number", "null"],
          }),
          recordEditorFieldSchema(["boolean"], {
            type: ["boolean", "null"],
          }),
          recordEditorFieldSchema(
            ["tags"],
            governedSelectionValueSchema("tags"),
            true,
          ),
          recordEditorFieldSchema(
            ["files", "media"],
            governedSelectionValueSchema("files"),
            true,
          ),
          recordEditorFieldSchema(
            ["relation"],
            governedSelectionValueSchema("relations"),
            true,
          ),
          recordEditorFieldSchema(["json"], GOVERNED_JSON_VALUE_SCHEMA),
        ],
      },
    },
  },
});

const RECORD_HISTORY_SCHEMA =
  "monkeys-design://ontology-view/record-history/v3";
const RECORD_HISTORY_RENDER_MODEL_SCHEMA = jsonSchema(RECORD_HISTORY_SCHEMA, {
  type: "object",
  additionalProperties: false,
  required: [
    "aggregateId",
    "headCommitId",
    "commits",
    "pagination",
    "reason",
    "rollback",
    "deletion",
  ],
  properties: {
    title: { type: "string" },
    titleKey: { type: "string", minLength: 1 },
    presentation: { enum: ["panel", "dialog", "data-explorer-dialog"] },
    closeLabelKey: { type: "string", minLength: 1 },
    aggregateId: { type: "string", minLength: 1, maxLength: 256 },
    recordTitle: { type: "string" },
    headCommitId: { type: "string", minLength: 1, maxLength: 256 },
    selectedTargetCommitId: {
      type: "string",
      minLength: 1,
      maxLength: 256,
    },
    commits: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["commitId", "createdAt"],
        properties: {
          commitId: { type: "string", minLength: 1, maxLength: 256 },
          label: { type: "string" },
          labelKey: { type: "string", minLength: 1 },
          actorLabel: { type: "string" },
          actorAccount: { type: "string" },
          createdAt: { type: "string", minLength: 1, maxLength: 128 },
          updatedTimestamp: { type: "integer", minimum: 0 },
          summary: { type: "string", maxLength: 1024 },
          systemMessage: { type: "string", maxLength: 2048 },
          parentCommitIds: {
            type: "array",
            items: { type: "string", minLength: 1, maxLength: 256 },
          },
          changes: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["fieldId"],
              properties: {
                fieldId: { type: "string", minLength: 1, maxLength: 256 },
                label: { type: "string" },
                labelKey: { type: "string", minLength: 1 },
                before: {},
                after: {},
              },
            },
          },
          disabled: { type: "boolean" },
        },
      },
    },
    pagination: cursorPaginationSchema(),
    diff: {
      type: "object",
      additionalProperties: false,
      required: ["items"],
      properties: {
        items: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["fieldId"],
            properties: {
              fieldId: { type: "string", minLength: 1, maxLength: 256 },
              label: { type: "string" },
              labelKey: { type: "string", minLength: 1 },
              before: { type: ["string", "number", "boolean", "null"] },
              after: { type: ["string", "number", "boolean", "null"] },
            },
          },
        },
      },
    },
    reason: { type: "string", maxLength: 2048 },
    messageEditor: {
      type: "object",
      additionalProperties: false,
      required: ["commitId", "message", "pending"],
      properties: {
        commitId: { type: "string", minLength: 1, maxLength: 256 },
        message: { type: "string", maxLength: 500 },
        pending: { type: "boolean" },
        errorCode: { type: "string", minLength: 1, maxLength: 128 },
      },
    },
    rollback: {
      type: "object",
      additionalProperties: false,
      required: ["enabled", "pending"],
      properties: {
        enabled: { type: "boolean" },
        pending: { type: "boolean" },
        errorCode: { type: "string", minLength: 1, maxLength: 128 },
      },
    },
    deletion: {
      type: "object",
      additionalProperties: false,
      required: ["enabled", "pending", "confirmationRequired"],
      properties: {
        enabled: { type: "boolean" },
        pending: { type: "boolean" },
        confirmationRequired: { const: true },
        errorCode: { type: "string", minLength: 1, maxLength: 128 },
      },
    },
  },
});

const RECORD_HISTORY_INTENT_SCHEMAS = Object.freeze({
  close: jsonSchema("monkeys-design://intent/record-history-close/v1", {
    type: "object",
    additionalProperties: false,
    properties: {},
  }),
  loadMore: cursorIntentSchema(
    "monkeys-design://intent/record-history-load-more/v1",
  ),
  select: jsonSchema("monkeys-design://intent/record-history-select/v1", {
    type: "object",
    additionalProperties: false,
    required: ["targetCommitId"],
    properties: {
      targetCommitId: { type: "string", minLength: 1, maxLength: 256 },
    },
  }),
  reasonChange: jsonSchema(
    "monkeys-design://intent/record-history-reason-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["reason"],
      properties: { reason: { type: "string", maxLength: 2048 } },
    },
  ),
  messageEdit: jsonSchema(
    "monkeys-design://intent/record-history-message-edit/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["commitId", "expectedUpdatedTimestamp", "message"],
      properties: {
        commitId: { type: "string", minLength: 1, maxLength: 256 },
        expectedUpdatedTimestamp: { type: "integer", minimum: 0 },
        message: { type: "string", maxLength: 500 },
      },
    },
  ),
  messageChange: jsonSchema(
    "monkeys-design://intent/record-history-message-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["message"],
      properties: { message: { type: "string", maxLength: 500 } },
    },
  ),
  messageCancel: jsonSchema(
    "monkeys-design://intent/record-history-message-cancel/v1",
    { type: "object", additionalProperties: false, properties: {} },
  ),
  messageUpdate: jsonSchema(
    "monkeys-design://intent/record-history-message-update/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["commitId", "expectedUpdatedTimestamp", "message"],
      properties: {
        commitId: { type: "string", minLength: 1, maxLength: 256 },
        expectedUpdatedTimestamp: { type: "integer", minimum: 0 },
        message: { type: "string", maxLength: 500 },
      },
    },
  ),
  rollback: jsonSchema("monkeys-design://intent/record-history-rollback/v1", {
    type: "object",
    additionalProperties: false,
    required: [
      "aggregateId",
      "targetCommitId",
      "expectedHeadCommitId",
      "reason",
    ],
    properties: {
      aggregateId: { type: "string", minLength: 1, maxLength: 256 },
      targetCommitId: { type: "string", minLength: 1, maxLength: 256 },
      expectedHeadCommitId: {
        type: "string",
        minLength: 1,
        maxLength: 256,
      },
      reason: { type: "string", maxLength: 2048 },
    },
  }),
  delete: jsonSchema("monkeys-design://intent/record-history-delete/v1", {
    type: "object",
    additionalProperties: false,
    required: ["aggregateId", "expectedHeadCommitId", "reason"],
    properties: {
      aggregateId: { type: "string", minLength: 1, maxLength: 256 },
      expectedHeadCommitId: {
        type: "string",
        minLength: 1,
        maxLength: 256,
      },
      reason: { type: "string", minLength: 1, maxLength: 2048 },
    },
  }),
});

const GALLERY_ACTIVATE_INTENT_SCHEMA = jsonSchema(
  "monkeys-design://intent/gallery-activate/v1",
  {
    type: "object",
    additionalProperties: false,
    required: ["itemId"],
    properties: {
      itemId: { type: "string", minLength: 1 },
    },
  },
);

const GALLERY_LOAD_MORE_INTENT_SCHEMA = jsonSchema(
  "monkeys-design://intent/gallery-load-more/v1",
  {
    type: "object",
    additionalProperties: false,
    required: ["cursor"],
    properties: {
      cursor: { type: "string", minLength: 1, maxLength: 2048 },
    },
  },
);

const GALLERY_SELECTION_CHANGE_INTENT_SCHEMA = jsonSchema(
  "monkeys-design://intent/gallery-selection-change/v1",
  {
    type: "object",
    additionalProperties: false,
    required: ["itemIds"],
    properties: {
      itemIds: {
        type: "array",
        uniqueItems: true,
        items: { type: "string", minLength: 1 },
      },
    },
  },
);

const LEGACY_GALLERY_SETTING_PROPERTIES: Record<string, JsonObject> = {
  columns: { oneOf: [{ const: "auto" }, { type: "integer", minimum: 1, maximum: 12 }] },
  aspectRatio: { enum: ["auto", "1:1", "4:3", "3:4", "16:9", "9:16"] }, showMetadata: { type: "boolean" },
};

const RECORD_EDITOR_SUBMIT_PROPERTIES: JsonObject = {
      draftRevision: { type: 'string', minLength: 1 },
      secretChanges: { type: 'array', items: { oneOf: [
        { type: 'object', additionalProperties: false, required: ['path', 'mode'], properties: { path: editorPathSchema, mode: { enum: ['keep', 'clear'] } } },
        { type: 'object', additionalProperties: false, required: ['path', 'mode', 'value'], properties: { path: editorPathSchema, mode: { const: 'replace' }, value: { type: ['string', 'number', 'boolean', 'object', 'array', 'null'] } } }
      ] } },
      values: {
        type: "object",
        additionalProperties: true,
      },
      featureValues: {
        type: "object",
        additionalProperties: GOVERNED_READ_VALUE_SCHEMA,
      },
    };

const DATA_CAPABILITY_INTENT_SCHEMAS = Object.freeze({
  navigatorSelect: itemIdIntentSchema(
    "monkeys-design://intent/ontology-navigator-select/v1",
    "nodeId",
  ),
  navigatorToggle: jsonSchema(
    "monkeys-design://intent/ontology-navigator-toggle/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["expandedNodeIds"],
      properties: {
        expandedNodeIds: {
          type: "array",
          uniqueItems: true,
          items: { type: "string", minLength: 1 },
        },
      },
    },
  ),
  navigatorLoadMore: cursorIntentSchema(
    "monkeys-design://intent/ontology-navigator-load-more/v1",
  ),
  navigatorSearch: jsonSchema(
    "monkeys-design://intent/ontology-navigator-search/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["value"],
      properties: { value: { type: "string" } },
    },
  ),
  navigatorRetry: jsonSchema(
    "monkeys-design://intent/ontology-navigator-retry/v1",
    {
      type: "object",
      additionalProperties: false,
      properties: {},
    },
  ),
  navigatorHome: jsonSchema(
    "monkeys-design://intent/ontology-navigator-home/v1",
    {
      type: "object",
      additionalProperties: false,
      properties: {},
    },
  ),
  navigatorDestination: jsonSchema(
    "monkeys-design://intent/ontology-navigator-destination/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["destinationId"],
      properties: { destinationId: { type: "string", minLength: 1 } },
    },
  ),
  navigatorAction: jsonSchema(
    "monkeys-design://intent/ontology-navigator-action/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["nodeId", "actionId"],
      properties: {
        nodeId: { type: "string", minLength: 1 },
        actionId: { type: "string", minLength: 1 },
      },
    },
  ),
  navigatorReorder: jsonSchema(
    "monkeys-design://intent/ontology-navigator-reorder/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["nodeId", "parentNodeId", "beforeNodeId"],
      properties: {
        nodeId: { type: "string", minLength: 1 },
        parentNodeId: {
          oneOf: [{ type: "string", minLength: 1 }, { type: "null" }],
        },
        beforeNodeId: {
          oneOf: [{ type: "string", minLength: 1 }, { type: "null" }],
        },
      },
    },
  ),
  professionalSelect: jsonSchema(
    "monkeys-design://intent/professional-leaf-select/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["itemId"],
      properties: {
        itemId: { type: "string", minLength: 1 },
        scopeId: { type: "string", minLength: 1 },
      },
    },
  ),
  professionalDismiss: jsonSchema(
    "monkeys-design://intent/professional-leaf-dismiss/v1",
    {
      type: "object",
      additionalProperties: false,
      properties: {},
    },
  ),
  toolbarSearch: jsonSchema("monkeys-design://intent/data-toolbar-search/v1", {
    type: "object",
    additionalProperties: false,
    required: ["value"],
    properties: { value: { type: "string" } },
  }),
  toolbarViewChange: itemIdIntentSchema(
    "monkeys-design://intent/data-toolbar-view-change/v1",
    "viewId",
  ),
  toolbarRefresh: jsonSchema(
    "monkeys-design://intent/data-toolbar-refresh/v1",
    {
      type: "object",
      additionalProperties: false,
    },
  ),
  toolbarAddRow: jsonSchema("monkeys-design://intent/data-toolbar-add-row/v1", {
    type: "object",
    additionalProperties: false,
    required: ["blankRowLabel", "requestedAt"],
    properties: {
      blankRowLabel: { type: "string", minLength: 1, maxLength: 256 },
      requestedAt: { type: "integer", minimum: 0 },
    },
  }),
  toolbarCommand: itemIdIntentSchema(
    "monkeys-design://intent/data-toolbar-command/v1",
    "commandId",
  ),
  toolbarFilterChange: jsonSchema(
    "monkeys-design://intent/data-toolbar-filter-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["filterId", "values"],
      properties: {
        filterId: { type: "string", minLength: 1 },
        values: {
          type: "array",
          uniqueItems: true,
          items: GOVERNED_READ_VALUE_SCHEMA,
        },
      },
    },
  ),
  toolbarGallerySettingsChange: jsonSchema(
    "monkeys-design://intent/data-toolbar-gallery-settings-change/v1",
    { type: "object", oneOf: [GALLERY_PRESENTATION_SETTING_CHANGE_SCHEMA,
      ...Object.entries(LEGACY_GALLERY_SETTING_PROPERTIES).map(([settingId, value]) => ({ type: "object", additionalProperties: false, required: ["settingId", "value"], properties: { settingId: { const: settingId }, value } })),
    ] },
  ),
  toolbarRowHeightChange: jsonSchema(
    "monkeys-design://intent/data-toolbar-row-height-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["mode", "preset", "scale"],
      properties: {
        mode: { enum: ["preset", "custom"] },
        preset: { enum: ["compact", "default", "comfortable"] },
        scale: { type: "number", minimum: 0.5, maximum: 10, multipleOf: 0.5 },
      },
    },
  ),
  toolbarColumnConfigurationChange: jsonSchema(
    "monkeys-design://intent/data-toolbar-column-configuration-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["expectedDefinitionVersion", "changes"],
      properties: {
        expectedDefinitionVersion: { type: "integer", minimum: 1 },
        changes: {
          type: "object",
          additionalProperties: false,
          required: ["displayConfig"],
          properties: {
            displayConfig: { type: "object", additionalProperties: true },
          },
        },
      },
    },
  ),
  toolbarTrendMatrixConfigurationChange: jsonSchema(
    "monkeys-design://intent/data-toolbar-trend-matrix-configuration-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["expectedDefinitionVersion", "changes"],
      properties: {
        expectedDefinitionVersion: { type: "integer", minimum: 1 },
        changes: {
          type: "object",
          additionalProperties: false,
          required: ["displayConfig"],
          properties: {
            displayConfig: { type: "object", additionalProperties: true },
          },
        },
      },
    },
  ),
  tableActivate: itemIdIntentSchema(
    "monkeys-design://intent/ontology-table-activate/v1",
    "rowId",
  ),
  tableDelete: itemIdIntentSchema(
    "monkeys-design://intent/ontology-table-delete/v1",
    "rowId",
  ),
  tableSelection: jsonSchema(
    "monkeys-design://intent/ontology-table-selection-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["rowIds"],
      properties: {
        rowIds: {
          type: "array",
          uniqueItems: true,
          items: { type: "string", minLength: 1 },
        },
      },
    },
  ),
  tableSort: jsonSchema("monkeys-design://intent/ontology-table-sort/v1", {
    type: "object",
    additionalProperties: false,
    required: ["columnId", "sortBy", "direction"],
    properties: {
      columnId: { type: "string", minLength: 1 },
      sortBy: { enum: ["asset_id", "updated_timestamp", "pin_order"] },
      direction: { enum: ["asc", "desc"] },
    },
  }),
  tableLoadMore: cursorIntentSchema(
    "monkeys-design://intent/ontology-table-load-more/v1",
  ),
  tableCellIntent: jsonSchema(
    "monkeys-design://intent/ontology-table-cell/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["rowId", "columnId", "intentId"],
      properties: {
        rowId: { type: "string", minLength: 1 },
        columnId: { type: "string", minLength: 1 },
        intentId: { type: "string", minLength: 1 },
        value: GOVERNED_READ_VALUE_SCHEMA,
      },
    },
  ),
  tableRowAction: jsonSchema(
    "monkeys-design://intent/ontology-table-row-action/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["rowId", "actionId"],
      properties: {
        rowId: { type: "string", minLength: 1 },
        context: { type: "object" },
        actionId: { type: "string", minLength: 1 },
      },
    },
  ),
  tableColumnAction: jsonSchema(
    "monkeys-design://intent/ontology-table-column-action/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["columnId", "actionId"],
      properties: {
        columnId: { type: "string", minLength: 1 },
        actionId: { type: "string", minLength: 1 },
      },
    },
  ),
  cardActivate: itemIdIntentSchema(
    "monkeys-design://intent/card-grid-activate/v1",
  ),
  cardDelete: itemIdIntentSchema("monkeys-design://intent/card-grid-delete/v1"),
  cardSelection: jsonSchema(
    "monkeys-design://intent/card-grid-selection-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["itemIds"],
      properties: {
        itemIds: {
          type: "array",
          uniqueItems: true,
          items: { type: "string", minLength: 1 },
        },
      },
    },
  ),
  cardRowAction: jsonSchema("monkeys-design://intent/card-grid-row-action/v1", {
    type: "object",
    additionalProperties: false,
    required: ["itemId", "actionId"],
    properties: {
      itemId: { type: "string", minLength: 1 },
      actionId: { type: "string", minLength: 1 },
      payload: { type: "object" },
    },
  }),
  cardLoadMore: cursorIntentSchema(
    "monkeys-design://intent/card-grid-load-more/v1",
  ),
  collectionPageSizeChange: jsonSchema(
    "monkeys-design://intent/collection-page-size-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["pageSize"],
      properties: {
        pageSize: { enum: [20, 50, 100, 200, 500, 1000] },
      },
    },
  ),
  paginationPageChange: jsonSchema(
    "monkeys-design://intent/pagination-page-change/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["page"],
      properties: { page: { type: "integer", minimum: 1 } },
    },
  ),
  paginationLoadMore: cursorIntentSchema(
    "monkeys-design://intent/pagination-load-more/v1",
  ),
  recordEdit: jsonSchema("monkeys-design://intent/record-detail-edit/v1", {
    type: "object",
    additionalProperties: false,
    properties: {},
  }),
  recordClose: jsonSchema("monkeys-design://intent/record-detail-close/v1", {
    type: "object",
    additionalProperties: false,
    properties: {},
  }),
  recordViewed: jsonSchema("monkeys-design://intent/record-detail-viewed/v1", {
    type: "object",
    additionalProperties: false,
    required: ["itemId"],
    properties: {
      itemId: { type: "string", minLength: 1 },
    },
  }),
  recordNavigate: jsonSchema(
    "monkeys-design://intent/record-detail-navigate/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["direction", "targetId"],
      properties: {
        direction: { enum: ["previous", "next"] },
        targetId: { type: "string", minLength: 1 },
      },
    },
  ),
  recordAction: jsonSchema("monkeys-design://intent/record-detail-action/v1", {
    type: "object",
    additionalProperties: false,
    required: ["actionId"],
    properties: {
      actionId: { type: "string", minLength: 1 },
      itemId: { type: "string", minLength: 1 },
    },
  }),
  recordPreviewDownload: jsonSchema(
    "monkeys-design://intent/record-detail-preview-download/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["itemId", "source"],
      properties: {
        itemId: { type: "string", minLength: 1 },
        source: { type: "string", minLength: 1 },
      },
    },
  ),
  editorChange: jsonSchema("monkeys-design://intent/record-editor-change/v1", {
    type: "object",
    additionalProperties: false,
    required: ["fieldId", "value"],
    properties: {
      fieldId: { type: "string" },
      draft: { type: 'object', additionalProperties: false, required: ['revision', 'value', 'dirty', 'touched'], properties: { revision: { type: 'string' }, value: { type: 'object' }, dirty: { type: 'boolean' }, touched: { type: 'array', items: editorPathSchema } } },
      value: GOVERNED_READ_VALUE_SCHEMA,
    },
  }),
  editorPick: jsonSchema('monkeys-design://intent/record-editor-pick/v1', { type: 'object', additionalProperties: false, required: ['fieldId', 'draftValues', 'draftRevision'], properties: { fieldId: { type: 'string', minLength: 1 }, draftValues: { type: 'object' }, draftRevision: { type: 'string', minLength: 1 } } }),
  editorDraftPatchRejected: jsonSchema('monkeys-design://intent/record-editor-draft-patch-rejected/v1', { type: 'object', additionalProperties: false, required: ['key', 'reason'], properties: { key: { type: 'string' }, reason: { enum: ['stale-revision', 'stale-value', 'invalid-operation'] } } }),
  editorCancel: jsonSchema("monkeys-design://intent/record-editor-cancel/v1", {
    type: "object",
    additionalProperties: false,
    properties: {},
  }),
  editorSubmit: jsonSchema("monkeys-design://intent/record-editor-submit/v1", {
    type: "object", additionalProperties: false, required: ["values", "featureValues"], properties: RECORD_EDITOR_SUBMIT_PROPERTIES,
  }),
  editorAuxiliarySubmit: jsonSchema("monkeys-design://intent/record-editor-auxiliary-submit/v1", {
    type: "object", additionalProperties: false, required: ["actionId", "values", "featureValues"],
    properties: { ...RECORD_EDITOR_SUBMIT_PROPERTIES, actionId: { type: "string", minLength: 1, maxLength: 256 } },
  }),
  editorOptionsRequest: jsonSchema('monkeys-design://intent/record-editor-options-request/v1', {
    type: 'object', additionalProperties: false, required: ['fieldId'], properties: { fieldId: { type: 'string', minLength: 1, maxLength: 256 } },
  }),
  editorOptionsLoadMore: jsonSchema(
    "monkeys-design://intent/record-editor-options-load-more/v1",
    {
      type: "object",
      additionalProperties: false,
      required: ["fieldId", "cursor"],
      properties: {
        fieldId: { type: "string", minLength: 1, maxLength: 256 },
        cursor: { type: "string", minLength: 1, maxLength: 2048 },
      },
    },
  ),
});

const LOCALIZED_COPY_PROPERTIES = {
  title: { type: "string" },
  titleKey: { type: "string", minLength: 1 },
  description: { type: "string" },
  descriptionKey: { type: "string", minLength: 1 },
} as const;

const LOCALIZED_COPY_PROPERTY_FIELDS = [
  {
    path: "title",
    label: i18n("Title", "标题"),
    control: "text",
    required: false,
    advanced: false,
  },
  {
    path: "titleKey",
    label: i18n("Title i18n key", "标题 i18n 键"),
    control: "i18n-key",
    required: false,
    advanced: true,
  },
  {
    path: "description",
    label: i18n("Description", "说明"),
    control: "textarea",
    required: false,
    advanced: false,
  },
  {
    path: "descriptionKey",
    label: i18n("Description i18n key", "说明 i18n 键"),
    control: "i18n-key",
    required: false,
    advanced: true,
  },
] satisfies readonly PropertyFieldDefinition[];

const SECTION_HEADER_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/section-header/v1",
  {
    type: "object",
    additionalProperties: false,
    properties: {
      eyebrow: { type: "string" },
      eyebrowKey: { type: "string", minLength: 1 },
      ...LOCALIZED_COPY_PROPERTIES,
    },
  },
);

const NOTICE_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/notice/v1",
  {
    type: "object",
    additionalProperties: false,
    properties: {
      tone: {
        enum: ["default", "primary", "success", "warning", "danger", "muted"],
      },
      ...LOCALIZED_COPY_PROPERTIES,
    },
  },
);

const DESCRIPTION_LIST_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/description-list/v1",
  {
    type: "object",
    additionalProperties: false,
    required: ["items"],
    properties: {
      columns: { enum: [1, 2, 3] },
      label: { type: "string" },
      labelKey: { type: "string", minLength: 1 },
      items: {
        type: "array",
        items: { oneOf: [
          { type: 'object', additionalProperties: false, required: ['id', 'valueType', 'value'], properties: {
            id: { type: 'string', minLength: 1 }, term: { type: 'string' }, termKey: { type: 'string', minLength: 1 }, locale: { type: 'string', minLength: 2, maxLength: 64 }, valueType: { const: 'timestamp' }, value: { type: ['string', 'number'] },
          } }, {
          type: "object",
          additionalProperties: false,
          required: ["id"],
          properties: {
            id: { type: "string", minLength: 1 },
            term: { type: "string" },
            termKey: { type: "string", minLength: 1 },
            value: { type: "string" },
            valueKey: { type: "string", minLength: 1 },
          },
        }] },
      },
    },
  },
);

const CONTENT_PANEL_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/content-panel/v1",
  {
    type: "object",
    additionalProperties: false,
    properties: {
      ...LOCALIZED_COPY_PROPERTIES,
      selected: { type: "boolean" },
      disabled: { type: "boolean" },
      density: { enum: ["compact", "default", "comfortable"] },
    },
  },
);

const TOOLBAR_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/toolbar/v1",
  {
    type: "object",
    additionalProperties: false,
    properties: LOCALIZED_COPY_PROPERTIES,
  },
);

const STATUS_RENDER_MODEL_SCHEMA = jsonSchema(
  "monkeys-design://render-model/status/v2",
  {
    type: "object",
    additionalProperties: false,
    required: ["kind"],
    properties: {
      kind: { enum: ["loading", "empty", "error", "forbidden"] },
      presentation: { enum: ["legacy-data-explorer"] },
      code: { type: "string", minLength: 1 },
      titleKey: { type: "string", minLength: 1 },
      descriptionKey: { type: "string", minLength: 1 },
      noticeKey: { type: "string", minLength: 1 },
      emptyLabelKey: { type: "string", minLength: 1 },
      retryable: { type: "boolean" },
    },
  },
);

const PROFESSIONAL_RENDER_MODEL_SCHEMA = jsonSchema(PROFESSIONAL_SCHEMA, {
  type: "object",
  additionalProperties: true,
});

export const NEUTRAL_CAPABILITY_CATALOG_ENTRIES = Object.freeze([
  CHOICE_PICKER_ENTRY,
  createEntry({
    id: DESIGN_CAPABILITY_IDS.summaryChart,
    authoringRevision: 2, schemaRevision: 2,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.summaryChart,
    kind: 'view', category: 'data-view',
    labelKey: 'monkeys.design.capability.summaryChart.label',
    descriptionKey: 'monkeys.design.capability.summaryChart.description',
    authoringLabel: i18n('Summary chart', '汇总图表'),
    authoringDescription: i18n('Compare bounded numeric series and category totals.', '比较数值序列与分类汇总。'),
    inputPort: 'chart', schemaRef: SUMMARY_CHART_SCHEMA, schemaDocument: SUMMARY_CHART_RENDER_MODEL_SCHEMA,
    slots: [], outputPorts: [], loading: 'on-activation', supportedPageTypes: ['page', 'view', 'workspace', 'preview'], supportedSurfaces: ['page', 'view', 'workspace', 'overlay'],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.shellLayout,
    version: "18",
    revision: 18,
    schemaRevision: 4,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.shellLayout,
    kind: "view",
    category: "layout",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.shellLayout.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.shellLayout.description,
    authoringLabel: i18n("Page layout", "页面布局"),
    authoringDescription: i18n(
      "Arrange shared header, sidebar, main, aside, and footer slots.",
      "装配共享页头、侧栏、主区域、辅助区域和页脚。",
    ),
    inputPort: "layout",
    schemaRef: "monkeys-design://render-model/shell-layout/v3",
    schemaDocument: SHELL_LAYOUT_RENDER_MODEL_SCHEMA,
    slots: [
      "header",
      "notices",
      "sidebar",
      "main",
      "aside",
      "footer",
      "overlay",
    ],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        variant: {
          enum: ["default", "data-explorer", "region-stack"],
          default: "default",
        },
        fullHeight: { type: "boolean", default: false },
        sidebarLabelKey: { type: "string" },
        mobileSidebar: { enum: ["stack", "drawer"], default: "stack" },
        asideLabelKey: { type: "string" },
        sidebarCollapsed: { type: "boolean", default: false },
        sidebarCollapsible: { type: "boolean", default: true },
        sidebarResizable: { type: "boolean", default: true },
        sidebarWidth: {
          type: "integer",
          minimum: 200,
          maximum: 600,
          default: 256,
        },
        asideWidth: {
          type: "integer",
          minimum: 320,
          maximum: 560,
          default: 400,
        },
        asideResizable: { type: "boolean", default: true },
        appearance: DATA_EXPLORER_APPEARANCE_SCHEMA,
      },
    },
    uiSchema: {
      order: [
        "variant",
        "fullHeight",
        "sidebarCollapsed",
        "sidebarCollapsible",
        "sidebarResizable",
        "sidebarWidth",
        "asideWidth",
        "asideResizable",
        "appearance",
        "sidebarLabelKey",
        "mobileSidebar",
        "asideLabelKey",
      ],
    },
    propertyFields: [
      {
        path: "variant",
        label: i18n("Layout variant", "布局类型"),
        control: "select",
        required: false,
        advanced: false,
      },
      {
        path: "fullHeight",
        label: i18n("Use full height", "使用全高布局"),
        control: "switch",
        required: false,
        advanced: false,
      },
      {
        path: "sidebarLabelKey",
        label: i18n("Sidebar label key", "侧栏标签键"),
        control: "i18n-key",
        required: false,
        advanced: true,
      },
      {
        path: "mobileSidebar",
        label: i18n("Narrow sidebar presentation", "窄屏侧栏展示"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "asideLabelKey",
        label: i18n("Aside label key", "辅助区域标签键"),
        control: "i18n-key",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.surface",
        label: i18n("Data Explorer surface", "数据页面外壳"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.preset",
        label: i18n("Data Explorer appearance preset", "数据页面外观预设"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.density",
        label: i18n("Data Explorer density", "数据页面密度"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.radius",
        label: i18n("Data Explorer corner radius", "数据页面圆角"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.border",
        label: i18n("Data Explorer border", "数据页面边框"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "appearance.background",
        label: i18n("Data Explorer background", "数据页面背景"),
        control: "select",
        required: false,
        advanced: true,
      },
      {
        path: "sidebarCollapsed",
        label: i18n("Collapse sidebar", "收起侧栏"),
        control: "switch",
        required: false,
        advanced: false,
      },
      {
        path: "sidebarCollapsible",
        label: i18n("Allow sidebar collapse", "允许折叠侧栏"),
        control: "switch",
        required: false,
        advanced: false,
      },
      {
        path: "sidebarResizable",
        label: i18n("Allow sidebar resize", "允许调整侧栏宽度"),
        control: "switch",
        required: false,
        advanced: false,
      },
      {
        path: "sidebarWidth",
        label: i18n("Sidebar width", "侧栏宽度"),
        control: "number",
        required: false,
        advanced: true,
      },
      {
        path: "asideWidth",
        label: i18n("Detail width", "详情区域宽度"),
        control: "number",
        required: false,
        advanced: true,
      },
      {
        path: "asideResizable",
        label: i18n("Allow detail resize", "允许调整详情宽度"),
        control: "switch",
        required: false,
        advanced: false,
      },
    ],
    outputPorts: [
      {
        name: "capability.shell-layout.sidebar-collapse",
        schemaRef: "monkeys-design://intent/shell-layout-sidebar-collapse/v1",
        schemaDocument: SHELL_LAYOUT_INTENT_SCHEMAS.sidebarCollapse,
      },
      {
        name: "capability.shell-layout.sidebar-resize",
        schemaRef: "monkeys-design://intent/shell-layout-sidebar-resize/v1",
        schemaDocument: SHELL_LAYOUT_INTENT_SCHEMAS.sidebarResize,
      },
      {
        name: "capability.shell-layout.aside-resize",
        schemaRef: "monkeys-design://intent/shell-layout-aside-resize/v1",
        schemaDocument: SHELL_LAYOUT_INTENT_SCHEMAS.asideResize,
      },
    ],
    supportedPageTypes: ["page", "workspace", "preview"],
    supportedSurfaces: ["page", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.sidebarFilter,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.sidebarFilter,
    kind: "view",
    category: "navigation",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.sidebarFilter.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.sidebarFilter.description,
    authoringLabel: i18n("Filter sidebar", "筛选侧栏"),
    authoringDescription: i18n(
      "Filter an Ontology view with searchable, governed options.",
      "使用可搜索、受治理的选项筛选 Ontology 视图。",
    ),
    inputPort: "ontologyView",
    schemaRef: SIDEBAR_FILTER_SCHEMA,
    schemaDocument: SIDEBAR_FILTER_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: SIDEBAR_FILTER_SCHEMA,
        mode: "filter",
      },
    ],
    slots: ["header", "footer"],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.ontologyNavigator,
    version: "23",
    revision: 23,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.ontologyNavigator,
    kind: "view",
    category: "navigation",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyNavigator.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyNavigator.description,
    authoringLabel: i18n("Ontology navigator", "本体导航器"),
    authoringDescription: i18n(
      "Navigate a governed hierarchical Ontology view.",
      "浏览受治理的分层本体视图。",
    ),
    inputPort: "ontologyView",
    schemaRef: ONTOLOGY_NAVIGATOR_SCHEMA,
    schemaDocument: ONTOLOGY_NAVIGATOR_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: ONTOLOGY_NAVIGATOR_SCHEMA,
        mode: "view",
      },
    ],
    slots: ["header", "footer"],
    outputPorts: [
      {
        name: "capability.ontology-navigator.select",
        schemaRef: "monkeys-design://intent/ontology-navigator-select/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorSelect,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.toggle",
        schemaRef: "monkeys-design://intent/ontology-navigator-toggle/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorToggle,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.load-more",
        schemaRef: "monkeys-design://intent/ontology-navigator-load-more/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorLoadMore,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.search",
        schemaRef: "monkeys-design://intent/ontology-navigator-search/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorSearch,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.retry",
        schemaRef: "monkeys-design://intent/ontology-navigator-retry/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorRetry,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.home",
        schemaRef: "monkeys-design://intent/ontology-navigator-home/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorHome,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.destination",
        schemaRef: "monkeys-design://intent/ontology-navigator-destination/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorDestination,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-navigator.action",
        schemaRef: "monkeys-design://intent/ontology-navigator-action/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorAction,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
      {
        name: "capability.ontology-navigator.reorder",
        schemaRef: "monkeys-design://intent/ontology-navigator-reorder/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.navigatorReorder,
        allowedCommandKinds: ["domain-command"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
    virtualized: true,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.dataToolbar,
    version: "27",
    revision: 27,
    schemaRevision: 2,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.dataToolbar,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.dataToolbar.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.dataToolbar.description,
    authoringLabel: i18n("Data toolbar", "数据工具栏"),
    authoringDescription: i18n(
      "Compose governed search, view switching, and data commands.",
      "装配受治理的搜索、视图切换和数据命令。",
    ),
    inputPort: "controls",
    schemaRef: DATA_TOOLBAR_SCHEMA,
    schemaDocument: DATA_TOOLBAR_RENDER_MODEL_SCHEMA,
    slots: ["leading", "content", "actions", "secondaryActions"],
    outputPorts: [
      {
        name: "capability.data-toolbar.search",
        schemaRef: "monkeys-design://intent/data-toolbar-search/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarSearch,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.view-change",
        schemaRef: "monkeys-design://intent/data-toolbar-view-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarViewChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.refresh",
        schemaRef: "monkeys-design://intent/data-toolbar-refresh/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarRefresh,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.add-row",
        schemaRef: "monkeys-design://intent/data-toolbar-add-row/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarAddRow,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.data-toolbar.command",
        schemaRef: "monkeys-design://intent/data-toolbar-command/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarCommand,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.data-toolbar.filter-change",
        schemaRef: "monkeys-design://intent/data-toolbar-filter-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarFilterChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.gallery-settings-change",
        schemaRef:
          "monkeys-design://intent/data-toolbar-gallery-settings-change/v1",
        schemaDocument:
          DATA_CAPABILITY_INTENT_SCHEMAS.toolbarGallerySettingsChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.row-height-change",
        schemaRef: "monkeys-design://intent/data-toolbar-row-height-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.toolbarRowHeightChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-toolbar.column-configuration-change",
        schemaRef:
          "monkeys-design://intent/data-toolbar-column-configuration-change/v1",
        schemaDocument:
          DATA_CAPABILITY_INTENT_SCHEMAS.toolbarColumnConfigurationChange,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.data-toolbar.trend-matrix-configuration-change",
        schemaRef:
          "monkeys-design://intent/data-toolbar-trend-matrix-configuration-change/v1",
        schemaDocument:
          DATA_CAPABILITY_INTENT_SCHEMAS.toolbarTrendMatrixConfigurationChange,
        allowedCommandKinds: ["domain-command"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.ontologyTable,
    version: "31",
    revision: 31,
    schemaRevision: 3,
    sideEffects: ["clipboard"],
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.ontologyTable,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyTable.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyTable.description,
    authoringLabel: i18n("Ontology table", "本体表格"),
    authoringDescription: i18n(
      "Present governed Ontology records in a sortable, selectable table.",
      "以可排序、可选择的表格展示受治理的本体记录。",
    ),
    inputPort: "ontologyView",
    schemaRef: ONTOLOGY_TABLE_SCHEMA,
    schemaDocument: ONTOLOGY_TABLE_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: ONTOLOGY_TABLE_SCHEMA,
        mode: "view",
      },
    ],
    slots: ["header", "footer"],
    outputPorts: [
      {
        name: "capability.ontology-table.activate",
        schemaRef: "monkeys-design://intent/ontology-table-activate/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableActivate,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.selection-change",
        schemaRef: "monkeys-design://intent/ontology-table-selection-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableSelection,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.sort",
        schemaRef: "monkeys-design://intent/ontology-table-sort/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableSort,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.load-more",
        schemaRef: "monkeys-design://intent/ontology-table-load-more/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableLoadMore,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.page-size-change",
        schemaRef: "monkeys-design://intent/collection-page-size-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.collectionPageSizeChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.page-change",
        schemaRef: "monkeys-design://intent/pagination-page-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.paginationPageChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-table.cell-intent",
        schemaRef: "monkeys-design://intent/ontology-table-cell/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableCellIntent,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
      {
        name: "capability.ontology-table.row-action",
        schemaRef: "monkeys-design://intent/ontology-table-row-action/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableRowAction,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
      {
        name: "capability.ontology-table.delete",
        schemaRef: "monkeys-design://intent/ontology-table-delete/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableDelete,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.ontology-table.column-action",
        schemaRef: "monkeys-design://intent/ontology-table-column-action/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.tableColumnAction,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
    virtualized: true,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.filePicker, rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.filePicker,
    version: '2',
    revision: 2,
    authoringRevision: 2,
    schemaRevision: 2,
    kind: 'view', category: 'data-editor', labelKey: 'monkeys.design.capability.filePicker.label', descriptionKey: 'monkeys.design.capability.filePicker.description',
    authoringLabel: i18n('File picker', '文件选择器'), authoringDescription: i18n('Select a local file for a governed action.', '选择本地文件以执行受治理操作。'),
    inputPort: 'filePicker', slots: [], schemaRef: FILE_PICKER_SCHEMA, schemaDocument: FILE_PICKER_RENDER_MODEL_SCHEMA,
    outputPorts: [
      { name: 'capability.file-picker.select', schemaRef: 'monkeys-design://intent/file-picker-select/v1', schemaDocument: FILE_PICKER_SELECT_INTENT_SCHEMA, allowedCommandKinds: ['query-intent'] },
      { name: 'capability.file-picker.cancel', schemaRef: 'monkeys-design://intent/file-picker-cancel/v1', schemaDocument: FILE_PICKER_CANCEL_INTENT_SCHEMA, allowedCommandKinds: ['query-intent'] },
    ], supportedPageTypes: ['page', 'view', 'workspace'], supportedSurfaces: ['page', 'view', 'workspace', 'overlay'],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.numericSeries,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.numericSeries,
    kind: "view", category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.numericSeries.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.numericSeries.description,
    authoringLabel: i18n("Numeric series", "数值序列"),
    authoringDescription: i18n("Inspect numeric observations with range zoom and expansion.", "查看数值观测，缩放范围并放大图表。"),
    inputPort: "series",
    slots: [],
    schemaRef: NUMERIC_SERIES_SCHEMA,
    schemaDocument: NUMERIC_SERIES_RENDER_MODEL_SCHEMA,
    outputPorts: [
      { name: "capability.numeric-series.range-change", schemaRef: "monkeys-design://intent/numeric-series-range/v1", schemaDocument: NUMERIC_SERIES_RANGE_INTENT_SCHEMA, allowedCommandKinds: ["query-intent"] },
      { name: "capability.numeric-series.expand", schemaRef: "monkeys-design://intent/numeric-series-expand/v1", schemaDocument: NUMERIC_SERIES_EXPAND_INTENT_SCHEMA, allowedCommandKinds: ["query-intent"] },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace", "overlay"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.ontologyTrendMatrix,
    version: "7",
    revision: 7,
    authoringRevision: 7,
    schemaRevision: 3,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.ontologyTrendMatrix,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyTrendMatrix.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyTrendMatrix.description,
    authoringLabel: i18n("Ontology trend matrix", "本体趋势矩阵"),
    authoringDescription: i18n(
      "Compare bounded Ontology trend rows and columns with governed cell selection.",
      "通过受治理的单元格选择，对比有界的本体趋势行列。",
    ),
    inputPort: "ontologyView",
    schemaRef: ONTOLOGY_TREND_MATRIX_SCHEMA,
    schemaDocument: ONTOLOGY_TREND_MATRIX_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: ONTOLOGY_TREND_MATRIX_SCHEMA,
        mode: "view",
      },
    ],
    slots: ["header", "footer"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        presentation: {
          type: "object",
          additionalProperties: false,
          required: ["kind"],
          properties: {
            kind: { const: "legacy-data-explorer" },
            loadingLabelKey: { type: "string", minLength: 1 },
            emptyLabelKey: { type: "string", minLength: 1 },
            errorLabelKey: { type: "string", minLength: 1 },
          },
        },
      },
    },
    uiSchema: { order: ["presentation"] },
    outputPorts: [
      {
        name: "capability.ontology-trend-matrix.select",
        schemaRef: "monkeys-design://intent/ontology-trend-matrix-select/v1",
        schemaDocument: ONTOLOGY_TREND_MATRIX_SELECT_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.ontology-trend-matrix.page-size-change",
        schemaRef: "monkeys-design://intent/collection-page-size-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.collectionPageSizeChange,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
    virtualized: true,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.dataContextBar,
    version: "9",
    revision: 9,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.dataContextBar,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.dataContextBar.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.dataContextBar.description,
    authoringLabel: i18n(
      "Data filter and drilldown context",
      "数据筛选与钻取上下文",
    ),
    authoringDescription: i18n(
      "Show safe active query context and emit governed clear or change intents.",
      "展示安全的当前查询上下文，并发出受治理的清除或变更意图。",
    ),
    inputPort: "controls",
    schemaRef: DATA_CONTEXT_BAR_SCHEMA,
    schemaDocument: DATA_CONTEXT_BAR_RENDER_MODEL_SCHEMA,
    slots: ["leading", "content", "actions"],
    outputPorts: [
      {
        name: "capability.data-context-bar.clear",
        schemaRef: "monkeys-design://intent/data-context-bar-clear/v1",
        schemaDocument: DATA_CONTEXT_BAR_CLEAR_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-context-bar.change",
        schemaRef: "monkeys-design://intent/data-context-bar-change/v1",
        schemaDocument: DATA_CONTEXT_BAR_CHANGE_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.data-context-bar.refresh",
        schemaRef: "monkeys-design://intent/data-context-bar-refresh/v1",
        schemaDocument: DATA_CONTEXT_BAR_REFRESH_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.cardGrid,
    version: "11",
    revision: 11,
    schemaRevision: 2,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.cardGrid,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.cardGrid.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.cardGrid.description,
    authoringLabel: i18n("Card grid", "卡片网格"),
    authoringDescription: i18n(
      "Present governed Ontology records as a selectable card grid.",
      "将受治理的本体记录展示为可选择的卡片网格。",
    ),
    inputPort: "ontologyView",
    schemaRef: CARD_GRID_SCHEMA,
    schemaDocument: CARD_GRID_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: CARD_GRID_SCHEMA,
        mode: "view",
      },
    ],
    slots: ["header", "footer"],
    outputPorts: [
      {
        name: "capability.card-grid.activate",
        schemaRef: "monkeys-design://intent/card-grid-activate/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.cardActivate,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.card-grid.selection-change",
        schemaRef: "monkeys-design://intent/card-grid-selection-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.cardSelection,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.card-grid.row-action",
        schemaRef: "monkeys-design://intent/card-grid-row-action/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.cardRowAction,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
      {
        name: "capability.card-grid.delete",
        schemaRef: "monkeys-design://intent/card-grid-delete/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.cardDelete,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.card-grid.load-more",
        schemaRef: "monkeys-design://intent/card-grid-load-more/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.cardLoadMore,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.card-grid.page-size-change",
        schemaRef: "monkeys-design://intent/collection-page-size-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.collectionPageSizeChange,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
    virtualized: true,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.gallery,
    version: "14",
    revision: 14,
    schemaRevision: 2,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.gallery,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.gallery.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.gallery.description,
    authoringLabel: i18n("Gallery", "画廊"),
    authoringDescription: i18n(
      "Present a paginated Ontology view as a responsive gallery.",
      "将分页 Ontology 视图展示为响应式画廊。",
    ),
    inputPort: "ontologyView",
    schemaRef: GALLERY_SCHEMA,
    schemaDocument: GALLERY_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: GALLERY_SCHEMA,
        mode: "view",
      },
    ],
    slots: ["header", "footer"],
    outputPorts: [
      {
        name: "capability.gallery.row-action",
        schemaRef: "monkeys-design://intent/gallery-row-action/v1",
        schemaDocument: jsonSchema("monkeys-design://intent/gallery-row-action/v1", {
          type: "object", additionalProperties: false, required: ["itemId", "actionId"],
          properties: { context: { type: "object" }, itemId: { type: "string", minLength: 1 }, actionId: { type: "string", minLength: 1 }, payload: { type: "object" } },
        }),
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.gallery.activate",
        schemaRef: "monkeys-design://intent/gallery-activate/v1",
        schemaDocument: GALLERY_ACTIVATE_INTENT_SCHEMA,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.gallery.selection-change",
        schemaRef: "monkeys-design://intent/gallery-selection-change/v1",
        schemaDocument: GALLERY_SELECTION_CHANGE_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.gallery.load-more",
        schemaRef: "monkeys-design://intent/gallery-load-more/v1",
        schemaDocument: GALLERY_LOAD_MORE_INTENT_SCHEMA,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.gallery.page-size-change",
        schemaRef: "monkeys-design://intent/collection-page-size-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.collectionPageSizeChange,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
    virtualized: true,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.pagination,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.pagination,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.pagination.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.pagination.description,
    authoringLabel: i18n("Pagination", "分页"),
    authoringDescription: i18n(
      "Navigate governed page or cursor pagination without owning data fetching.",
      "导航受治理的页码或游标分页，但不拥有数据获取。",
    ),
    inputPort: "pageInfo",
    schemaRef: PAGINATION_SCHEMA,
    schemaDocument: PAGINATION_RENDER_MODEL_SCHEMA,
    slots: [],
    outputPorts: [
      {
        name: "capability.pagination.page-change",
        schemaRef: "monkeys-design://intent/pagination-page-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.paginationPageChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.pagination.load-more",
        schemaRef: "monkeys-design://intent/pagination-load-more/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.paginationLoadMore,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.recordDetail,
    version: "19",
    revision: 19,
    schemaRevision: 4,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.recordDetail,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.recordDetail.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.recordDetail.description,
    authoringLabel: i18n("Record detail", "记录详情"),
    authoringDescription: i18n(
      "Display typed Ontology record fields with an optional governed edit intent.",
      "展示类型化本体记录字段，并可发出受治理的编辑意图。",
    ),
    inputPort: "ontologyRecord",
    schemaRef: RECORD_DETAIL_SCHEMA,
    schemaDocument: RECORD_DETAIL_RENDER_MODEL_SCHEMA,
    slots: ["header", "footer"],
    sideEffects: ["clipboard"],
    outputPorts: [
      {
        name: "capability.record-detail.viewed",
        schemaRef: "monkeys-design://intent/record-detail-viewed/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordViewed,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.record-detail.edit",
        schemaRef: "monkeys-design://intent/record-detail-edit/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordEdit,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-detail.close",
        schemaRef: "monkeys-design://intent/record-detail-close/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordClose,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-detail.navigate",
        schemaRef: "monkeys-design://intent/record-detail-navigate/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordNavigate,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-detail.action",
        schemaRef: "monkeys-design://intent/record-detail-action/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordAction,
        allowedCommandKinds: ["query-intent", "domain-command"],
      },
      {
        name: "capability.record-detail.preview-download",
        schemaRef: "monkeys-design://intent/record-detail-preview-download/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.recordPreviewDownload,
        allowedCommandKinds: ["domain-command"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.recordEditor,
    version: "17",
    revision: 17,
    authoringRevision: 17,
    schemaRevision: 16,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.recordEditor,
    kind: "view",
    category: "data-editor",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.recordEditor.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.recordEditor.description,
    authoringLabel: i18n("Record editor", "记录编辑器"),
    authoringDescription: i18n(
      "Edit host-owned typed record state and emit governed change, cancel, and submit intents.",
      "编辑由宿主拥有的类型化记录状态，并发出受治理的变更、取消和提交意图。",
    ),
    inputPort: "ontologyEditor",
    schemaRef: RECORD_EDITOR_SCHEMA,
    schemaDocument: RECORD_EDITOR_RENDER_MODEL_SCHEMA,
    slots: ["header", "footer"],
    outputPorts: [
      { name: 'capability.record-editor.options-request', schemaRef: 'monkeys-design://intent/record-editor-options-request/v1', schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorOptionsRequest, allowedCommandKinds: ['query-intent'] },
      { name: "capability.record-editor.auxiliary-submit", schemaRef: "monkeys-design://intent/record-editor-auxiliary-submit/v1", schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorAuxiliarySubmit, allowedCommandKinds: ["domain-command"] },
      {
        name: 'capability.record-editor.pick', schemaRef: 'monkeys-design://intent/record-editor-pick/v1', schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorPick, allowedCommandKinds: ['query-intent'],
      },
      {
        name: 'capability.record-editor.draftPatchRejected', schemaRef: 'monkeys-design://intent/record-editor-draft-patch-rejected/v1', schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorDraftPatchRejected, allowedCommandKinds: ['query-intent'],
      },
      {
        name: "capability.record-editor.change",
        schemaRef: "monkeys-design://intent/record-editor-change/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-editor.cancel",
        schemaRef: "monkeys-design://intent/record-editor-cancel/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorCancel,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-editor.submit",
        schemaRef: "monkeys-design://intent/record-editor-submit/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorSubmit,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.record-editor.options-load-more",
        schemaRef: "monkeys-design://intent/record-editor-options-load-more/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.editorOptionsLoadMore,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.recordHistory,
    version: "17",
    revision: 17,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.recordHistory,
    kind: "view",
    category: "data-view",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.recordHistory.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.recordHistory.description,
    authoringLabel: i18n("Record history and diff", "记录历史与差异"),
    authoringDescription: i18n(
      "Review exact record commits, compare a selected target, enter a reason, and emit a governed rollback intent.",
      "查看精确记录提交、比较选中目标、填写原因，并发出受治理的回滚意图。",
    ),
    inputPort: "recordHistory",
    schemaRef: RECORD_HISTORY_SCHEMA,
    schemaDocument: RECORD_HISTORY_RENDER_MODEL_SCHEMA,
    slots: ["header", "footer"],
    sideEffects: ["clipboard"],
    outputPorts: [
      {
        name: "capability.record-history.close",
        schemaRef: "monkeys-design://intent/record-history-close/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.close,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.load-more",
        schemaRef: "monkeys-design://intent/record-history-load-more/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.loadMore,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.select",
        schemaRef: "monkeys-design://intent/record-history-select/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.select,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.reason-change",
        schemaRef: "monkeys-design://intent/record-history-reason-change/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.reasonChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.message-edit",
        schemaRef: "monkeys-design://intent/record-history-message-edit/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.messageEdit,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.message-change",
        schemaRef: "monkeys-design://intent/record-history-message-change/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.messageChange,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.message-cancel",
        schemaRef: "monkeys-design://intent/record-history-message-cancel/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.messageCancel,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.record-history.message-update",
        schemaRef: "monkeys-design://intent/record-history-message-update/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.messageUpdate,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.record-history.rollback",
        schemaRef: "monkeys-design://intent/record-history-rollback/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.rollback,
        allowedCommandKinds: ["domain-command"],
      },
      {
        name: "capability.record-history.delete",
        schemaRef: "monkeys-design://intent/record-history-delete/v1",
        schemaDocument: RECORD_HISTORY_INTENT_SCHEMAS.delete,
        allowedCommandKinds: ["domain-command"],
      },
    ],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.ontologyManagement,
    version: "7",
    revision: 7,
    authoringRevision: 7,
    schemaRevision: 3,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.ontologyManagement,
    kind: "view",
    category: "data-editor",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyManagement.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.ontologyManagement.description,
    authoringLabel: i18n(
      "Ontology and view management actions",
      "本体与视图管理操作",
    ),
    authoringDescription: i18n(
      "Submit governed Ontology, View, Feature Column, managed-file import, and export commands with exact revision fences without owning resource APIs.",
      "提交带精确版本栅栏的受治理本体、视图、特征列、托管文件导入与导出命令，但不持有业务 API。",
    ),
    inputPort: "managementActions",
    schemaRef: ONTOLOGY_MANAGEMENT_SCHEMA,
    schemaDocument: ONTOLOGY_MANAGEMENT_RENDER_MODEL_SCHEMA,
    slots: ["header", "footer"],
    outputPorts: [...ONTOLOGY_MANAGEMENT_OUTPUT_PORTS],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.featureColumnManager,
    version: "4",
    revision: 4,
    authoringRevision: 4,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.featureColumnManager,
    kind: "view",
    category: "data-editor",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.featureColumnManager.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.featureColumnManager.description,
    authoringLabel: i18n("Feature column manager", "特征列管理器"),
    authoringDescription: i18n(
      "Manage versioned Feature Column definitions and View attachments through governed intents.",
      "通过受治理意图管理带版本的特征列定义与视图挂载。",
    ),
    inputPort: "catalog",
    schemaRef: FEATURE_COLUMN_MANAGER_SCHEMA,
    schemaDocument: FEATURE_COLUMN_MANAGER_RENDER_MODEL_SCHEMA,
    slots: [],
    outputPorts: [...FEATURE_COLUMN_MANAGER_OUTPUT_PORTS],
    supportedPageTypes: ["page", "view", "workspace"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.sectionHeader,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.sectionHeader,
    kind: "view",
    category: "content",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.sectionHeader.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.sectionHeader.description,
    authoringLabel: i18n("Section header", "分区标题"),
    authoringDescription: i18n(
      "Introduce a page section with localized eyebrow, title, description, and governed actions.",
      "使用本地化眉题、标题、说明和受治理操作引导页面分区。",
    ),
    inputPort: "content",
    schemaRef: "monkeys-design://render-model/section-header/v1",
    schemaDocument: SECTION_HEADER_RENDER_MODEL_SCHEMA,
    slots: ["actions"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: SECTION_HEADER_RENDER_MODEL_SCHEMA.properties,
    },
    uiSchema: {
      order: [
        "eyebrow",
        "eyebrowKey",
        "title",
        "titleKey",
        "description",
        "descriptionKey",
      ],
    },
    propertyFields: [
      {
        path: "eyebrow",
        label: i18n("Eyebrow", "眉题"),
        control: "text",
        required: false,
        advanced: false,
      },
      {
        path: "eyebrowKey",
        label: i18n("Eyebrow i18n key", "眉题 i18n 键"),
        control: "i18n-key",
        required: false,
        advanced: true,
      },
      ...LOCALIZED_COPY_PROPERTY_FIELDS,
    ],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.notice,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.notice,
    kind: "view",
    category: "content",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.notice.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.notice.description,
    authoringLabel: i18n("Notice", "提示"),
    authoringDescription: i18n(
      "Present a localized semantic notice with optional governed icon and action slots.",
      "展示本地化语义提示，并可装配受治理的图标和操作槽。",
    ),
    inputPort: "content",
    schemaRef: "monkeys-design://render-model/notice/v1",
    schemaDocument: NOTICE_RENDER_MODEL_SCHEMA,
    slots: ["icon", "actions"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: NOTICE_RENDER_MODEL_SCHEMA.properties,
    },
    uiSchema: {
      order: ["tone", "title", "titleKey", "description", "descriptionKey"],
    },
    propertyFields: [
      {
        path: "tone",
        label: i18n("Tone", "语气"),
        control: "select",
        required: false,
        advanced: false,
      },
      ...LOCALIZED_COPY_PROPERTY_FIELDS,
    ],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.descriptionList,
    version: "3",
    revision: 3,
    schemaRevision: 3,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.descriptionList,
    kind: "view",
    category: "content",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.descriptionList.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.descriptionList.description,
    authoringLabel: i18n("Description list", "描述列表"),
    authoringDescription: i18n(
      "Display localized term and value pairs in an accessible description list.",
      "以无障碍描述列表展示本地化术语和值。",
    ),
    inputPort: "content",
    schemaRef: "monkeys-design://render-model/description-list/v1",
    schemaDocument: DESCRIPTION_LIST_RENDER_MODEL_SCHEMA,
    slots: ["footer"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: DESCRIPTION_LIST_RENDER_MODEL_SCHEMA.properties,
    },
    uiSchema: { order: ["label", "labelKey", "columns", "items"] },
    propertyFields: [
      {
        path: "label",
        label: i18n("Accessible label", "无障碍标签"),
        control: "text",
        required: false,
        advanced: false,
      },
      {
        path: "labelKey",
        label: i18n("Accessible label i18n key", "无障碍标签 i18n 键"),
        control: "i18n-key",
        required: false,
        advanced: true,
      },
      {
        path: "columns",
        label: i18n("Columns", "列数"),
        control: "select",
        required: false,
        advanced: false,
      },
      {
        path: "items",
        label: i18n("Items", "条目"),
        control: "json",
        required: true,
        advanced: false,
      },
    ],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.contentPanel,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.contentPanel,
    kind: "view",
    category: "content",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.contentPanel.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.contentPanel.description,
    authoringLabel: i18n("Content panel", "内容面板"),
    authoringDescription: i18n(
      "Group localized content with explicit media, body, and footer slots.",
      "使用明确的媒体、正文和页脚槽组织本地化内容。",
    ),
    inputPort: "content",
    schemaRef: "monkeys-design://render-model/content-panel/v1",
    schemaDocument: CONTENT_PANEL_RENDER_MODEL_SCHEMA,
    slots: ["media", "body", "footer"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: CONTENT_PANEL_RENDER_MODEL_SCHEMA.properties,
    },
    uiSchema: {
      order: [
        "title",
        "titleKey",
        "description",
        "descriptionKey",
        "density",
        "selected",
        "disabled",
      ],
    },
    propertyFields: [
      ...LOCALIZED_COPY_PROPERTY_FIELDS,
      {
        path: "density",
        label: i18n("Density", "密度"),
        control: "select",
        required: false,
        advanced: false,
      },
      {
        path: "selected",
        label: i18n("Selected", "已选中"),
        control: "switch",
        required: false,
        advanced: true,
      },
      {
        path: "disabled",
        label: i18n("Disabled", "已禁用"),
        control: "switch",
        required: false,
        advanced: true,
      },
    ],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.toolbar,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.toolbar,
    kind: "view",
    category: "content",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.toolbar.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.toolbar.description,
    authoringLabel: i18n("Toolbar", "工具栏"),
    authoringDescription: i18n(
      "Arrange localized context with governed leading, content, and action slots.",
      "组织本地化上下文以及受治理的前置、内容和操作槽。",
    ),
    inputPort: "content",
    schemaRef: "monkeys-design://render-model/toolbar/v1",
    schemaDocument: TOOLBAR_RENDER_MODEL_SCHEMA,
    slots: ["leading", "content", "actions"],
    propertySchema: {
      type: "object",
      additionalProperties: false,
      properties: TOOLBAR_RENDER_MODEL_SCHEMA.properties,
    },
    uiSchema: { order: ["title", "titleKey", "description", "descriptionKey"] },
    propertyFields: [...LOCALIZED_COPY_PROPERTY_FIELDS],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "view", "workspace"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.status,
    version: "7",
    revision: 7,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.status,
    kind: "view",
    category: "status",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.status.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.status.description,
    authoringLabel: i18n("Status", "状态"),
    authoringDescription: i18n(
      "Display governed loading, empty, forbidden, and error states.",
      "展示受治理的加载、空、无权限和错误状态。",
    ),
    inputPort: "status",
    schemaRef: "monkeys-design://render-model/status/v2",
    schemaDocument: STATUS_RENDER_MODEL_SCHEMA,
    slots: ["actions"],
    supportedPageTypes: ["page", "view", "overlay", "preview"],
    supportedSurfaces: ["page", "view", "overlay"],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.professionalLeaf,
    version: "2",
    revision: 2,
    authoringRevision: 3,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.professionalLeaf,
    kind: "professional-provider",
    category: "professional",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.professionalLeaf.label,
    descriptionKey:
      DESIGN_CAPABILITY_I18N_KEYS.catalog.professionalLeaf.description,
    authoringLabel: i18n("Professional component", "专业组件"),
    authoringDescription: i18n(
      "Embed a governed specialist runtime as a bounded page component.",
      "将受治理的专业运行时作为有边界的页面组件嵌入。",
    ),
    inputPort: "ontologyView",
    schemaRef: PROFESSIONAL_SCHEMA,
    schemaDocument: PROFESSIONAL_RENDER_MODEL_SCHEMA,
    compatibility: [
      {
        inputPort: "ontologyView",
        schemaRef: PROFESSIONAL_SCHEMA,
        mode: "view",
      },
    ],
    outputPorts: [
      {
        name: "capability.professional-leaf.select",
        schemaRef: "monkeys-design://intent/professional-leaf-select/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.professionalSelect,
        allowedCommandKinds: ["query-intent"],
      },
      {
        name: "capability.professional-leaf.dismiss",
        schemaRef: "monkeys-design://intent/professional-leaf-dismiss/v1",
        schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.professionalDismiss,
        allowedCommandKinds: ["query-intent"],
      },
    ],
    slots: ["toolbar", "footer"],
    loading: "on-activation",
    sideEffects: ["network", "worker", "websocket"],
    supportedPageTypes: [
      "design-board",
      "process",
      "agent",
      "enhanced",
      "global-design-board",
    ],
    supportedSurfaces: ["page", "workspace", "overlay"],
    frameOwner: "provider",
    editorEligible: false,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.professionalExtensionLeaf,
    version: "1",
    revision: 1,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.professionalExtensionLeaf,
    kind: "professional-provider",
    category: "professional",
    labelKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.professionalLeaf.label,
    descriptionKey: DESIGN_CAPABILITY_I18N_KEYS.catalog.professionalLeaf.description,
    authoringLabel: i18n("Professional extension panel", "专业扩展面板"),
    authoringDescription: i18n("Embed one governed extension surface as a bounded page panel.", "将一个受治理的扩展界面作为有边界的页面面板嵌入。"),
    inputPort: "ontologyView",
    schemaRef: PROFESSIONAL_SCHEMA,
    schemaDocument: PROFESSIONAL_RENDER_MODEL_SCHEMA,
    compatibility: [{ inputPort: "ontologyView", schemaRef: PROFESSIONAL_SCHEMA, mode: "view" }],
    outputPorts: [{ name: "capability.professional-leaf.dismiss", schemaRef: "monkeys-design://intent/professional-leaf-dismiss/v1", schemaDocument: DATA_CAPABILITY_INTENT_SCHEMAS.professionalDismiss, allowedCommandKinds: ["query-intent"] }],
    slots: ["toolbar", "footer"],
    loading: "on-activation",
    sideEffects: ["network", "worker", "websocket"],
    supportedPageTypes: ["page", "view", "workspace", "preview"],
    supportedSurfaces: ["page", "workspace", "overlay"],
    frameOwner: "provider",
    editorEligible: false,
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.orderedSelection,
    version: "2", revision: 2, schemaRevision: 2,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.orderedSelection,
    kind: 'view', category: 'data-editor',
    labelKey: 'monkeys.design.capability.orderedSelection.label', descriptionKey: 'monkeys.design.capability.orderedSelection.description',
    authoringLabel: i18n('Ordered selection', '有序选择'), authoringDescription: i18n('Edit a bounded ordered selection without fetching candidates.', '编辑有界有序选择，候选数据由页面提供。'),
    inputPort: 'model', schemaRef: 'monkeys-design://ordered-selection/v2', schemaDocument: jsonSchema('monkeys-design://ordered-selection/v2', ORDERED_SELECTION_MODEL_SCHEMA),
    outputPorts: Object.entries(ORDERED_SELECTION_INTENT_SCHEMAS).map(([name, document]) => ({ name: `capability.ordered-selection.${name}`, schemaRef: `monkeys-design://ordered-selection-${name}/v1`, schemaDocument: jsonSchema(`monkeys-design://ordered-selection-${name}/v1`, document), allowedCommandKinds: ['query-intent', 'domain-command'] })),
    slots: [], supportedPageTypes: ['page','view','workspace'], supportedSurfaces: ['page','view','workspace'],
  }),
  createEntry({
    id: DESIGN_CAPABILITY_IDS.runtimeTerminal,
    rendererKey: DESIGN_CAPABILITY_RENDERER_KEYS.runtimeTerminal,
    kind: 'professional-provider', category: 'professional',
    labelKey: 'monkeys.design.runtime-terminal', descriptionKey: 'monkeys.design.runtime-terminal.description',
    authoringLabel: i18n('Terminal', '终端'), authoringDescription: i18n('Render an authorized terminal stream.', '显示已授权的终端流。'),
    inputPort: 'terminal', schemaRef: 'monkeys-design://runtime-terminal/v1',
    schemaDocument: jsonSchema('monkeys-design://runtime-terminal/v1', RUNTIME_TERMINAL_MODEL_SCHEMA),
    outputPorts: [
      { name: 'terminal.input', schemaRef: 'monkeys-design://terminal-input/v1', schemaDocument: jsonSchema('monkeys-design://terminal-input/v1', { type: 'object', additionalProperties: false, required: ['data'], properties: { data: { type: 'string', maxLength: 8192 } } }), allowedCommandKinds: ['query-intent'] },
      { name: 'terminal.resize', schemaRef: 'monkeys-design://terminal-resize/v1', schemaDocument: jsonSchema('monkeys-design://terminal-resize/v1', { type: 'object', additionalProperties: false, required: ['columns', 'rows'], properties: { columns: { type: 'integer', minimum: 1, maximum: 1000 }, rows: { type: 'integer', minimum: 1, maximum: 1000 } } }), allowedCommandKinds: ['query-intent'] },
      { name: 'stream.disconnect', schemaRef: 'monkeys-design://stream-disconnect/v1', schemaDocument: jsonSchema('monkeys-design://stream-disconnect/v1', { type: 'object', additionalProperties: false, properties: {} }), allowedCommandKinds: ['query-intent'] },
    ],
    slots: [], loading: 'on-activation', sideEffects: ['websocket'],
    supportedPageTypes: ['page', 'view', 'workspace'], supportedSurfaces: ['page', 'view', 'workspace'], frameOwner: 'provider',
  }),
]);

export const NEUTRAL_DESIGN_CAPABILITY_CATALOG = compileDesignCapabilityCatalog(
  NEUTRAL_CAPABILITY_CATALOG_ENTRIES,
);

export const NEUTRAL_DESIGN_CAPABILITY_CATALOG_ARTIFACT =
  compileDesignCapabilityCatalogArtifact({
    catalog: NEUTRAL_DESIGN_CAPABILITY_CATALOG,
  });

export { createEntry as createDesignCapabilityEntry };
